import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../components/AppDialog';
import { api } from '../utils/api';
import { openBillingPortal } from '../utils/stripe';
import RocketLoader from '../components/RocketLoader';
import { TEMPLATES, CV_TEMPLATES, SAMPLE_RESUME, SAMPLE_CL, EMPTY_CL, CL_TEMPLATES } from '../data/samples';
import ResumePreview from '../components/ResumePreview';
import { exportToPDF } from '../utils/exportUtils';
import { normalizeUserProfile, getProfileFullName, getProfileStrength, getProfileStrengthTier } from '../utils/profile';
import { InterviewsPage } from './InterviewsPage';
import { JobSearchPage } from './JobSearchPage';
import { JobTrackerPage } from '../job-tracker/JobTrackerPage';
import { LinkedInAuditPage } from './LinkedInAuditPage';
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  Search,
  CheckCircle2,
  ClipboardList,
  Clock,
  CreditCard,
  Crown,
  Download,
  FilePlus2,
  FileText,
  FolderOpen,
  GraduationCap,
  KeyRound,
  LayoutDashboard,
  Link2,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Pencil,
  Plus,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  UserRound,
  UserX,
  X,
  Zap,
  Gift,
  Users,
  Copy,
  CheckCheck,
  TrendingUp,
  HeadphonesIcon,
  Linkedin,
  KanbanSquare,
} from 'lucide-react';

const DOC_TYPES = [
  { type: 'resume',       label: 'Resume',       icon: '📄', desc: 'Professional resume for job applications', color: '#EF1D28', bg: '#FFF1E8', gradient: 'linear-gradient(135deg,#EF1D28,#C5161D)' },
  { type: 'cover_letter', label: 'Cover Letter',  icon: '✉️', desc: 'Personalized cover letter for any role',   color: '#059669', bg: '#F0FDF4', gradient: 'linear-gradient(135deg,#059669,#047857)' },
  { type: 'cv',           label: 'Academic CV',   icon: '🎓', desc: 'Detailed CV for academic & research roles', color: '#FB923C', bg: '#FFE7D6', gradient: 'linear-gradient(135deg,#FB923C,#EA580C)' },
];

const typeIcon  = { resume: '📄', cover_letter: '✉️', cv: '🎓' };
const typeLabel = { resume: 'Resume', cover_letter: 'Cover Letter', cv: 'Academic CV' };

const PREMIUM_SECTION_LABELS = {
  experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  strengths: 'Key Strengths',
  goals: 'Career Goals',
  hobbies: 'Hobbies & Interests',
  achievements: 'Achievements',
  certifications: 'Certifications',
  awards: 'Awards',
  languages: 'Languages',
  volunteering: 'Volunteering',
  publications: 'Publications',
  references: 'References',
  customBlocks: 'Custom Blocks',
  personalInfo: 'Your Information',
  jobInfo: 'Job Information',
  content: 'Letter Content',
  signature: 'Closing & Signature',
  letterDate: 'Letter Date',
  openingGreeting: 'Opening Greeting',
  closingSalutation: 'Closing Salutation',
};

const getPremiumSectionLabel = (id) => (
  PREMIUM_SECTION_LABELS[id] ||
  id.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())
);

const hasMeaningfulValue = (value) => {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
  return false;
};

const getUsedPremiumSections = (data = {}, premiumSections = []) => (
  premiumSections.filter((id) => {
    if (id === 'references' && data?.referencesAvailableOnRequest) return true;
    return hasMeaningfulValue(data?.[id]);
  })
);

const getDownloadFilename = (doc) => {
  const title = doc?.data?.personalInfo?.fullName || doc?.title || 'document';
  const type = doc?.type || 'resume';
  return `${title}-${type}`.replace(/[\\/:*?"<>|]+/g, '').trim() || `${type}-document`;
};

const getJobEmoji = (title = '') => {
  const t = title.toLowerCase();
  if (t.match(/engineer|developer|programmer|software|frontend|backend|fullstack/)) return '💻';
  if (t.match(/design|ux|ui/)) return '🎨';
  if (t.match(/data|analyst|analytics|scientist/)) return '📊';
  if (t.match(/market|seo|growth/)) return '📈';
  if (t.match(/sales|account executive/)) return '💰';
  if (t.match(/product/)) return '🚀';
  if (t.match(/finance|accountant|financial/)) return '💼';
  if (t.match(/hr|human resource|recruiter|talent/)) return '👥';
  if (t.match(/research|scientist|lab/)) return '🔬';
  if (t.match(/health|medical|nurse|doctor|clinical/)) return '🏥';
  if (t.match(/teach|professor|educat|instructor/)) return '🎓';
  if (t.match(/writer|content|editor|journalist/)) return '✍️';
  if (t.match(/legal|lawyer|attorney/)) return '⚖️';
  if (t.match(/operations|director|manager|lead|chief|officer|head/)) return '📋';
  return '💼';
};

const profileInput = {
  width: '100%',
  border: '1.5px solid #E8EDF2',
  borderRadius: 10,
  background: '#FAFBFC',
  padding: '10px 12px',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  color: '#1F2937',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const profileLabel = {
  display: 'block',
  color: '#6B7280',
  fontSize: 11,
  fontWeight: 600,
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const profileCard = {
  background: 'white',
  border: '1px solid #F0F2F5',
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
};

/* ═══════════════════════════════════════════════════════════════════════════
   REFERRAL TAB
═══════════════════════════════════════════════════════════════════════════ */
const ReferralTab = ({ user }) => {
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [copied, setCopied]         = useState(false);
  const [redeeming, setRedeeming]   = useState(false);
  const [redeemMsg, setRedeemMsg]   = useState('');
  const [redeemErr, setRedeemErr]   = useState('');
  const clientUrl = window.location.origin;

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await api.getReferralStats();
      setStats(data);
    } catch (e) {
      console.error('Referral stats error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const referralLink = stats?.code ? `${clientUrl}/signup?ref=${stats.code}` : '';

  const handleCopy = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareLinks = !referralLink ? [] : [
    {
      label: 'WhatsApp',
      color: '#25D366',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      href: `https://wa.me/?text=${encodeURIComponent(`Join MarsResume and get 7 days of Pro free! Sign up here: ${referralLink}`)}`,
    },
    {
      label: 'LinkedIn',
      color: '#0A66C2',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
    },
    {
      label: 'Twitter / X',
      color: '#000000',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.741l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Build a better resume with AI 🚀 Sign up via my referral link and get 7 days of Pro free: ${referralLink}`)}`,
    },
    {
      label: 'Email',
      color: '#EF1D28',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
      href: `mailto:?subject=${encodeURIComponent('Try MarsResume — 7 days Pro free')}&body=${encodeURIComponent(`Hey!\n\nI've been using MarsResume to build AI-powered resumes and thought you'd find it useful.\n\nUse my referral link to sign up and get 7 days of Pro for free:\n${referralLink}\n\nEnjoy!`)}`,
    },
  ];

  const handleRedeem = async () => {
    setRedeeming(true); setRedeemMsg(''); setRedeemErr('');
    try {
      const res = await api.redeemPoints();
      setRedeemMsg(res.message || '🎉 1 month Pro activated!');
      await fetchStats();
    } catch (e) {
      setRedeemErr(e.message);
    } finally {
      setRedeeming(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const isExpiringSoon = (d) => d && (new Date(d) - Date.now()) < 5 * 24 * 60 * 60 * 1000;

  const RED = '#EF1D28';

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'80px 0' }}>
      <Loader2 size={32} color={RED} style={{ animation:'spin 1s linear infinite' }} />
    </div>
  );

  const balance = stats?.balance || 0;
  const pct = Math.min((balance / 60) * 100, 100);
  const canRedeem = balance >= 60;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>

      {/* ── Hero Banner ── */}
      <div style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1A2332 100%)', borderRadius: 20, padding: '32px 36px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 80% 50%,rgba(239,29,40,0.2) 0%,transparent 55%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -40, right: 140, width: 220, height: 220, borderRadius: '50%', background: 'rgba(239,29,40,0.06)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -30, left: -20, width: 160, height: 160, borderRadius: '50%', background: 'rgba(251,146,60,0.06)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px,transparent 1px)', backgroundSize: '24px 24px', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(239,29,40,0.15)', border: '1px solid rgba(239,29,40,0.25)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: '#FCA5A5', fontWeight: 600, marginBottom: 12, letterSpacing: '0.03em' }}>
              <Gift size={11} /> Referral Program
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 30, fontWeight: 900, color: 'white', letterSpacing: -0.8, lineHeight: 1.1 }}>
              Refer friends,{' '}
              <span style={{ background: 'linear-gradient(90deg,#FF5C5C,#FB923C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>earn free Pro</span>
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, maxWidth: 420 }}>
              Share your link. Earn <strong style={{ color: '#FCA5A5' }}>5 pts</strong> per signup and <strong style={{ color: '#FCA5A5' }}>15 pts</strong> when they go Pro. Redeem <strong style={{ color: '#FCA5A5' }}>60 pts</strong> for 1 free Pro month.
            </p>
          </div>
          {/* Stats pills */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { val: stats?.totalSignups || 0,   label: 'Referred',   icon: <Users size={13} color="#93C5FD" />,     accent: 'rgba(59,130,246,0.15)',  border: 'rgba(59,130,246,0.2)' },
              { val: stats?.totalConverted || 0, label: 'Converted',  icon: <TrendingUp size={13} color="#34D399" />, accent: 'rgba(52,211,153,0.15)',  border: 'rgba(52,211,153,0.2)' },
              { val: stats?.totalEarned || 0,    label: 'Pts Earned', icon: <Zap size={13} color="#FBBF24" />,       accent: 'rgba(251,191,36,0.15)',  border: 'rgba(251,191,36,0.2)' },
            ].map(({ val, label, icon, accent, border }) => (
              <div key={label} style={{ background: accent, border: `1px solid ${border}`, borderRadius: 14, padding: '14px 20px', minWidth: 90, textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 6 }}>{icon}<span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 500, letterSpacing: '0.04em' }}>{label}</span></div>
                <div style={{ fontSize: 28, fontWeight: 900, color: 'white', lineHeight: 1, letterSpacing: -1 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Two-column: Points card + Share link ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 16, marginBottom: 20 }}>

        {/* Points balance card */}
        <div style={{ background: 'linear-gradient(145deg,#1E293B,#0F172A)', borderRadius: 18, padding: '24px 26px', color: 'white', display: 'flex', flexDirection: 'column', gap: 0, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: canRedeem ? 'rgba(34,197,94,0.1)' : 'rgba(239,29,40,0.08)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(99,102,241,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Current Balance</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, letterSpacing: -2, color: canRedeem ? '#4ADE80' : 'white' }}>{balance}</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>pts</span>
            </div>
            <div style={{ fontSize: 11.5, color: '#475569', marginBottom: 18 }}>Points expire 30 days after earned</div>

            {/* Progress */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 11 }}>
                <span style={{ color: '#64748B' }}>{balance} / 60 pts to redeem</span>
                <span style={{ color: canRedeem ? '#4ADE80' : '#94A3B8', fontWeight: 700 }}>{Math.round(pct)}%</span>
              </div>
              <div style={{ height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden', position: 'relative' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: canRedeem ? 'linear-gradient(90deg,#22C55E,#4ADE80)' : 'linear-gradient(90deg,#EC4899,#C084FC)', borderRadius: 99, transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)', boxShadow: canRedeem ? '0 0 12px rgba(34,197,94,0.5)' : '0 0 12px rgba(236,72,153,0.4)' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: '#334155' }}>
                <span>0</span><span style={{ color: canRedeem ? '#4ADE80' : '#EC4899', fontWeight: 700 }}>60 = 1 month free</span>
              </div>
            </div>

            {redeemMsg && <div style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: '9px 13px', fontSize: 12, color: '#86EFAC', marginBottom: 14 }}>{redeemMsg}</div>}
            {redeemErr && <div style={{ background: 'rgba(239,29,40,0.12)', border: '1px solid rgba(239,29,40,0.3)', borderRadius: 10, padding: '9px 13px', fontSize: 12, color: '#FCA5A5', marginBottom: 14 }}>{redeemErr}</div>}

            <button onClick={handleRedeem} disabled={!canRedeem || redeeming}
              style={{ display: 'inline-flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, border: 'none', fontFamily: 'inherit', fontWeight: 700, fontSize: 13, cursor: canRedeem && !redeeming ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
                background: canRedeem ? 'linear-gradient(135deg,#22C55E,#16A34A)' : 'rgba(255,255,255,0.05)',
                color: canRedeem ? 'white' : '#334155',
                boxShadow: canRedeem ? '0 4px 16px rgba(34,197,94,0.35)' : 'none',
                opacity: redeeming ? 0.7 : 1,
              }}>
              {redeeming ? <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }} /> : <Gift size={13} />}
              {canRedeem ? 'Redeem — Get 1 Month Free' : `${60 - balance} more pts needed`}
            </button>
          </div>
        </div>

        {/* Share link card */}
        <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '24px 26px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FFF1F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Link2 size={16} color="#EF1D28" />
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Your Referral Link</div>
          </div>
          <p style={{ margin: '0 0 18px', fontSize: 12.5, color: '#9CA3AF', lineHeight: 1.6 }}>
            Earn <span style={{ color: '#1D4ED8', fontWeight: 700 }}>+5 pts</span> per signup · <span style={{ color: '#059669', fontWeight: 700 }}>+15 pts</span> when they go Pro
          </p>

          {/* Link box */}
          <div style={{ background: '#F8FAFC', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '0 0 0 14px', display: 'flex', alignItems: 'center', marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ flex: 1, fontSize: 12, color: '#374151', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '11px 0' }}>
              {referralLink || 'Loading…'}
            </div>
            <button onClick={handleCopy} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 16px', borderLeft: '1.5px solid #E5E7EB', background: copied ? '#ECFDF5' : '#F1F5F9', color: copied ? '#059669' : '#374151', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', flexShrink: 0, border: 'none', borderLeft: '1.5px solid #E5E7EB' }}>
              {copied ? <><CheckCheck size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>

          {/* Share buttons */}
          <div style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Share via</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {shareLinks.map(({ label, color, icon, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 15px', borderRadius: 10, border: `1.5px solid ${color}22`, background: `${color}0D`, color, fontWeight: 700, fontSize: 12, textDecoration: 'none', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = 'white'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${color}40`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = `${color}0D`; e.currentTarget.style.color = color; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <span style={{ fontSize: 14 }}>{icon}</span> {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── How it works ── */}
      <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '26px 28px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF1F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={15} color="#EF1D28" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>How it works</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, position: 'relative' }}>
          {/* connector line */}
          <div style={{ position: 'absolute', top: 22, left: '17%', right: '17%', height: 2, background: 'linear-gradient(90deg,#1D4ED822,#05966922,#EF1D2822)', pointerEvents: 'none', zIndex: 0 }} />
          {[
            { step: '1', title: 'Share your link',       pts: null,     desc: 'Copy and share with friends, colleagues, or on social media.',         color: '#1D4ED8', grad: 'linear-gradient(135deg,#1D4ED8,#6366F1)', bg: '#EFF6FF', border: '#BFDBFE', icon: <Link2 size={14} color="#1D4ED8" /> },
            { step: '2', title: 'They sign up',          pts: '+5 pts', desc: 'Friend creates an account. You earn 5 pts — they get 7 days Pro free.', color: '#059669', grad: 'linear-gradient(135deg,#059669,#34D399)',  bg: '#ECFDF5', border: '#A7F3D0', icon: <Users size={14} color="#059669" /> },
            { step: '3', title: 'They upgrade to Pro',   pts: '+15 pts',desc: 'When they go Pro, you earn 15 more points. First upgrade only.',        color: RED,       grad: `linear-gradient(135deg,${RED},#FB923C)`, bg: '#FFF1F2', border: '#FECACA', icon: <Crown size={14} color={RED} /> },
          ].map(({ step, title, pts, desc, color, grad, bg, border, icon }) => (
            <div key={step} style={{ padding: '0 16px', position: 'relative', zIndex: 1, textAlign: 'center' }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', boxShadow: `0 6px 20px ${color}30` }}>
                <span style={{ fontSize: 16, fontWeight: 900, color: 'white' }}>{step}</span>
              </div>
              <div style={{ fontWeight: 800, fontSize: 13.5, color: '#111827', marginBottom: 6 }}>{title}</div>
              {pts && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, border: `1px solid ${border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 800, color, marginBottom: 8 }}>
                  {icon} {pts}
                </div>
              )}
              <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF', lineHeight: 1.6 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Referrals list ── */}
      {stats?.referrals?.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '24px 26px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={15} color="#1D4ED8" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Your Referrals</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 20, padding: '3px 10px' }}>{stats.referrals.length} total</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {stats.referrals.map((r, i) => (
              <div key={r._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: i > 0 ? '1px solid #F9FAFB' : 'none', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: r.status === 'converted' ? 'linear-gradient(135deg,#ECFDF5,#D1FAE5)' : r.status === 'signed_up' ? 'linear-gradient(135deg,#EFF6FF,#DBEAFE)' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: r.status === 'converted' ? '#059669' : r.status === 'signed_up' ? '#2563EB' : '#6B7280', flexShrink: 0, border: `1px solid ${r.status === 'converted' ? '#A7F3D0' : r.status === 'signed_up' ? '#BFDBFE' : '#E5E7EB'}` }}>
                    {(r.referredUserName || r.referredUserEmail || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.referredUserName || r.referredUserEmail || 'Anonymous'}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{r.signedUpAt ? `Joined ${fmtDate(r.signedUpAt)}` : 'Pending signup'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: r.status === 'converted' ? '#ECFDF5' : r.status === 'signed_up' ? '#EFF6FF' : '#F3F4F6', color: r.status === 'converted' ? '#059669' : r.status === 'signed_up' ? '#2563EB' : '#6B7280', border: `1px solid ${r.status === 'converted' ? '#A7F3D0' : r.status === 'signed_up' ? '#BFDBFE' : '#E5E7EB'}` }}>
                    {r.status === 'converted' ? '⭐ Pro' : r.status === 'signed_up' ? '✓ Signed Up' : '⏳ Pending'}
                  </span>
                  {r.signupPointsAwarded && <span style={{ fontSize: 11, color: '#059669', fontWeight: 800, background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 20, padding: '3px 8px' }}>+5 pts</span>}
                  {r.purchasePointsAwarded && <span style={{ fontSize: 11, color: '#059669', fontWeight: 800, background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 20, padding: '3px 8px' }}>+15 pts</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Points history ── */}
      {stats?.transactions?.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '24px 26px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF1F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={15} color={RED} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Points History</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {stats.transactions.map((t, i) => {
              const isEarned = t.type !== 'redeemed';
              const expired = isEarned && t.expiresAt && new Date(t.expiresAt) < new Date();
              const expiringSoon = isEarned && !expired && isExpiringSoon(t.expiresAt) && !t.used;
              const dimmed = t.used || expired;
              return (
                <div key={t._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: i > 0 ? '1px solid #F9FAFB' : 'none', opacity: dimmed ? 0.4 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: t.type === 'redeemed' ? '#FFF1F2' : '#F0FDF4', border: `1px solid ${t.type === 'redeemed' ? '#FECACA' : '#A7F3D0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      {t.type === 'redeemed' ? '🎁' : '⭐'}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t.description}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>
                        {fmtDate(t.createdAt)}
                        {expiringSoon && <span style={{ color: '#F59E0B', fontWeight: 700, marginLeft: 6 }}>⚠ Expires {fmtDate(t.expiresAt)}</span>}
                        {expired && <span style={{ marginLeft: 6 }}>· Expired</span>}
                        {t.used && !expired && <span style={{ marginLeft: 6 }}>· Used</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: t.type === 'redeemed' ? RED : '#059669', background: t.type === 'redeemed' ? '#FFF1F2' : '#F0FDF4', border: `1px solid ${t.type === 'redeemed' ? '#FECACA' : '#A7F3D0'}`, borderRadius: 20, padding: '4px 12px' }}>
                    {t.type === 'redeemed' ? '-' : '+'}{t.points} pts
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {(!stats?.referrals?.length && !stats?.transactions?.length) && (
        <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '52px 28px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: '#FFF1F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', fontSize: 32 }}>
            🎯
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginBottom: 8 }}>No referrals yet</div>
          <div style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 1.6, maxWidth: 320, margin: '0 auto' }}>
            Share your referral link above to start earning points and unlock free Pro months.
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileStrengthCard = ({ profile, docs = [], onSectionClick }) => {
  const { sections, percent } = getProfileStrength(profile, docs);
  const tier = getProfileStrengthTier(percent);
  const clickable = typeof onSectionClick === 'function';

  return (
    <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>Profile strength</span>
        <span title="Fill in every section to strengthen your profile and speed up autofill on new documents."
          style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px solid #D1D5DB', color: '#9CA3AF', fontSize: 9, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', flexShrink: 0 }}>?</span>
      </div>

      <div style={{ padding: '24px 18px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative', width: 128, height: 128 }}>
          <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: `conic-gradient(${tier.color} ${percent * 3.6}deg, #F1F5F9 0deg)` }} />
          <div style={{ position: 'absolute', inset: 11, borderRadius: '50%', background: 'white' }} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#111827', letterSpacing: -0.5, lineHeight: 1 }}>
              {percent}<span style={{ fontSize: 14, fontWeight: 700, color: '#9CA3AF' }}>%</span>
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.06em', marginTop: 4 }}>COMPLETE</div>
          </div>
        </div>
        <span style={{ marginTop: 14, marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 5, background: tier.bg, color: tier.color, border: `1px solid ${tier.border}`, borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 800 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: tier.color, display: 'inline-block' }} />{tier.label}
        </span>
      </div>

      <div style={{ borderTop: '1px solid #F3F4F6', padding: '8px 8px 10px' }}>
        {sections.map((s) => (
          <button key={s.id} type="button" disabled={!clickable} onClick={() => onSectionClick?.(s.id)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 10px', background: 'none', border: 'none', borderRadius: 10, cursor: clickable ? 'pointer' : 'default', fontFamily: 'inherit', textAlign: 'left' }}
            onMouseEnter={e => { if (clickable) e.currentTarget.style.background = '#FAFAFA'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
            <span style={{ fontSize: 13, fontWeight: s.done ? 700 : 500, color: s.done ? '#111827' : '#6B7280' }}>{s.label}</span>
            {s.done
              ? <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckCheck size={11} color="white" strokeWidth={3} />
                </div>
              : <div style={{ width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #E5E7EB', background: '#FAFAFA', flexShrink: 0 }} />}
          </button>
        ))}
      </div>
    </div>
  );
};

const ProfileTab = ({ user, onSave, onDeleted = () => {}, initialTab = 'profile', navNonce = 0, docs = [], initialSection = 'personal', onNavigateResume }) => {
  const { showAlert, showConfirm } = useDialog();
  const [profile, setProfile] = useState(() => normalizeUserProfile(user.userProfile, user));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState({ text: '', ok: false });
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [profileTab, setProfileTab] = useState(initialTab);
  const [profileSection, setProfileSection] = useState(initialSection);
  const fileRef = useRef(null);

  useEffect(() => { setProfileTab(initialTab); setProfileSection(initialSection); }, [navNonce, initialTab, initialSection]);

  useEffect(() => {
    setProfile(normalizeUserProfile(user.userProfile, user));
  }, [user]);

  const setField = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));
  const setPref = (key, value) => setProfile(prev => ({
    ...prev,
    jobPreferences: { ...prev.jobPreferences, [key]: value },
  }));

  const toggleEmployment = (value) => {
    setProfile(prev => {
      const current = prev.jobPreferences?.employmentTypes || [];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, jobPreferences: { ...prev.jobPreferences, employmentTypes: next } };
    });
  };

  const mkId = () => `${Date.now()}-${Math.floor(Math.random() * 10000)}`;

  const addExp   = () => setProfile(p => ({ ...p, experience: [...(p.experience||[]), { id: mkId(), title:'', company:'', location:'', startDate:'', endDate:'', current:false, description:'' }] }));
  const setExp   = (id, k, v) => setProfile(p => ({ ...p, experience: (p.experience||[]).map(e => e.id===id ? {...e, [k]:v} : e) }));
  const delExp   = id => setProfile(p => ({ ...p, experience: (p.experience||[]).filter(e => e.id!==id) }));

  const addEdu   = () => setProfile(p => ({ ...p, education: [...(p.education||[]), { id: mkId(), degree:'', school:'', location:'', startDate:'', endDate:'', gpa:'' }] }));
  const setEdu   = (id, k, v) => setProfile(p => ({ ...p, education: (p.education||[]).map(e => e.id===id ? {...e, [k]:v} : e) }));
  const delEdu   = id => setProfile(p => ({ ...p, education: (p.education||[]).filter(e => e.id!==id) }));

  const addSkill = () => setProfile(p => ({ ...p, skills: [...(p.skills||[]), { id: mkId(), category:'', items:'' }] }));
  const setSkill = (id, k, v) => setProfile(p => ({ ...p, skills: (p.skills||[]).map(s => s.id===id ? {...s, [k]:v} : s) }));
  const delSkill = id => setProfile(p => ({ ...p, skills: (p.skills||[]).filter(s => s.id!==id) }));

  const addCert  = () => setProfile(p => ({ ...p, certifications: [...(p.certifications||[]), { id: mkId(), name:'', issuer:'', date:'', credentialId:'' }] }));
  const setCert  = (id, k, v) => setProfile(p => ({ ...p, certifications: (p.certifications||[]).map(c => c.id===id ? {...c, [k]:v} : c) }));
  const delCert  = id => setProfile(p => ({ ...p, certifications: (p.certifications||[]).filter(c => c.id!==id) }));
  const addCourse = () => setProfile(p => ({ ...p, courses: [...(p.courses||[]), { id: mkId(), name:'', provider:'', date:'' }] }));
  const setCourse = (id, k, v) => setProfile(p => ({ ...p, courses: (p.courses||[]).map(c => c.id===id ? {...c, [k]:v} : c) }));
  const delCourse = id => setProfile(p => ({ ...p, courses: (p.courses||[]).filter(c => c.id!==id) }));

  const handlePhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setField('photo', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      showAlert('Profile save failed: ' + err.message, { type: 'error', title: 'Save Failed' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPwMessage({ text: '', ok: false });
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) {
      setPwMessage({ text: 'New password must be at least 6 characters.', ok: false });
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMessage({ text: 'New passwords do not match.', ok: false });
      return;
    }
    setPwSaving(true);
    try {
      await api.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwMessage({ text: 'Password updated successfully.', ok: true });
    } catch (err) {
      setPwMessage({ text: err.message, ok: false });
    } finally {
      setPwSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteText !== 'DELETE') return;
    const ok = await showConfirm('Delete your account and all documents permanently? This cannot be undone.', {
      title: 'Delete Account', confirmLabel: 'Delete Forever', danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.deleteAccount();
      onDeleted();
    } catch (err) {
      showAlert('Delete account failed: ' + err.message, { type: 'error', title: 'Error' });
      setDeleting(false);
    }
  };

  const fields = [
    ['firstName', 'First name'],
    ['lastName', 'Last name'],
    ['titleBefore', 'Title before'],
    ['titleAfter', 'Title after'],
    ['jobTitle', 'Job title / target role'],
    ['phone', 'Phone number'],
    ['email', 'Email address'],
    ['birthDate', 'Birth date', 'date'],
    ['nationality', 'Nationality'],
    ['street', 'Street, number'],
    ['city', 'City'],
    ['postalCode', 'Postal code'],
    ['country', 'Country'],
    ['website', 'Website'],
    ['linkedin', 'LinkedIn'],
  ];

  const preferences = profile.jobPreferences || {};
  const employmentTypes = preferences.employmentTypes || [];

  /* pill-style toggle helpers */
  const PillOption = ({ label, active, onClick }) => (
    <button type="button" onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${active ? '#EF1D28' : '#E8EDF2'}`,
      background: active ? '#FFF1F1' : 'white', color: active ? '#C5161D' : '#6B7280',
      fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit',
      transition: 'all 0.15s',
    }}>{label}</button>
  );

  const SectionHeading = ({ icon, title, subtitle }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #F3F4F6' }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#FFF1F1,#FFF8F0)', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
  );

  return (
    <div>
      {/* ── Page header with banner ── */}
      <div style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1E293B 100%)', borderRadius: 16, padding: '24px 28px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 80% 50%, rgba(239,29,40,0.15) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {profile.photo
                ? <img src={profile.photo} alt="Profile" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)' }} />
                : <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22, color: 'white', border: '2px solid rgba(255,255,255,0.2)' }}>
                    {getProfileFullName(profile, user).charAt(0).toUpperCase() || 'U'}
                  </div>}
              <div style={{ position: 'absolute', bottom: -3, right: -3, width: 14, height: 14, background: '#22C55E', borderRadius: '50%', border: '2px solid #1E293B' }} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'white', letterSpacing: -0.3 }}>{getProfileFullName(profile, user) || 'Your Name'}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{profile.jobTitle || 'Add your job title'}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: '2px 9px', textTransform: 'capitalize' }}>{user.plan || 'Free'} Plan</span>
              </div>
            </div>
          </div>
          {profileTab === 'profile' && (
            <button onClick={handleSave} disabled={saving} style={{
              background: saved ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#EF1D28,#FB923C)',
              color: saved ? '#4ADE80' : 'white',
              border: saved ? '1px solid rgba(74,222,128,0.3)' : 'none',
              borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 13,
              cursor: saving ? 'not-allowed' : 'pointer', minWidth: 120,
              boxShadow: saved ? 'none' : '0 4px 14px rgba(239,29,40,0.4)',
              transition: 'all 0.2s',
            }}>
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Profile'}
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, background: '#F3F4F6', borderRadius: 12, padding: 4, marginBottom: 24, width: 'fit-content' }}>
        {[['profile', 'Profile'], ['account', 'Account Settings']].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setProfileTab(id)} style={{
            padding: '8px 20px', borderRadius: 9, border: 'none',
            background: profileTab === id ? 'white' : 'transparent',
            color: profileTab === id ? '#111827' : '#6B7280',
            fontSize: 13, fontWeight: profileTab === id ? 700 : 500,
            cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: profileTab === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {/* ── Profile tab ── */}
      {profileTab === 'profile' && (() => {
        const iF = e => { e.target.style.borderColor = '#EF1D28'; e.target.style.boxShadow = '0 0 0 3px rgba(239,29,40,0.08)'; e.target.style.background = 'white'; };
        const iB = e => { e.target.style.borderColor = '#E8EDF2'; e.target.style.boxShadow = 'none'; e.target.style.background = '#FAFBFC'; };
        const iBS = e => { e.target.style.borderColor = '#E8EDF2'; e.target.style.boxShadow = 'none'; };
        const addBtnStyle = { background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 3px 10px rgba(239,29,40,0.25)' };
        const delBtnStyle = { background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
        const entryCard = { background: '#FAFBFC', border: '1px solid #F0F2F5', borderRadius: 14, padding: 20, marginBottom: 12 };
        const EmptyHint = ({ text }) => <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF', fontSize: 13 }}>{text}</div>;
        const Row2 = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>{children}</div>;
        const Field = ({ label, value, onChange, type = 'text', span2 = false, placeholder = '' }) => (
          <div style={span2 ? { gridColumn: 'span 2' } : {}}>
            <label style={profileLabel}>{label}</label>
            <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={profileInput} onFocus={iF} onBlur={iB} />
          </div>
        );

        const SECTIONS = [
          { id: 'personal',     label: 'Personal Details' },
          { id: 'preference',   label: 'Job Preference' },
          { id: 'experience',   label: 'Experience' },
          { id: 'education',    label: 'Education' },
          { id: 'skills',       label: 'Skills' },
          { id: 'courses',      label: 'Courses' },
          { id: 'certificates', label: 'Certificates' },
        ];

        const STRENGTH_SECTION_MAP = { basic: 'personal', summary: 'personal', skills: 'skills', experience: 'experience', education: 'education' };
        const handleStrengthClick = (id) => {
          if (id === 'resume') { onNavigateResume?.(); return; }
          setProfileSection(STRENGTH_SECTION_MAP[id] || 'personal');
        };

        return (
          <div className="profile-strength-layout">
            <style>{`
              .profile-strength-layout { display: grid; grid-template-columns: 1fr 300px; gap: 24px; align-items: start; }
              @media (max-width: 900px) { .profile-strength-layout { grid-template-columns: 1fr; } }
            `}</style>
            <div>
            {/* Sub-section nav */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 24, paddingBottom: 2, scrollbarWidth: 'none' }}>
              {SECTIONS.map(({ id, label }) => (
                <button key={id} type="button" onClick={() => setProfileSection(id)} style={{
                  padding: '8px 18px', borderRadius: 20, border: `1.5px solid ${profileSection===id ? '#EF1D28' : '#E5E7EB'}`,
                  background: profileSection===id ? '#FFF1F1' : 'white',
                  color: profileSection===id ? '#C5161D' : '#6B7280',
                  fontSize: 12, fontWeight: profileSection===id ? 700 : 500, cursor: 'pointer',
                  fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s', flexShrink: 0,
                }}>{label}</button>
              ))}
            </div>

            {/* ─ Personal Details ─ */}
            {profileSection === 'personal' && (
              <div style={profileCard}>
                <SectionHeading icon={<FileText size={16} color="#EF1D28" />} title="Personal Details" subtitle="Auto-filled into your documents as personalInfo" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#F8F9FA', borderRadius: 12, marginBottom: 18, border: '1px solid #F0F2F5' }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button type="button" onClick={() => fileRef.current?.click()} style={{ width: 64, height: 64, borderRadius: 14, border: 'none', background: '#D1D5DB', overflow: 'hidden', cursor: 'pointer', padding: 0, display: 'block' }}>
                      {profile.photo
                        ? <img src={profile.photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 22, fontWeight: 900 }}>{getProfileFullName(profile, user).charAt(0).toUpperCase() || 'U'}</div>}
                    </button>
                    <div style={{ position: 'absolute', bottom: -3, right: -3, width: 18, height: 18, borderRadius: '50%', background: '#EF1D28', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => fileRef.current?.click()}>
                      <span style={{ color: 'white', fontSize: 9, fontWeight: 900 }}>+</span>
                    </div>
                  </div>
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937', marginBottom: 6 }}>Profile Photo</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#6B7280', cursor: 'pointer', marginBottom: 6 }}>
                      <input type="checkbox" checked={!!profile.showPicture} onChange={e => setField('showPicture', e.target.checked)} style={{ accentColor: '#EF1D28' }} />
                      Show in new documents
                    </label>
                    {profile.photo && <button type="button" onClick={() => setField('photo', '')} style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 7, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Remove</button>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {fields.map(([key, label, type]) => (
                    <div key={key} style={['jobTitle','phone','email','website','linkedin'].includes(key) ? { gridColumn: 'span 2' } : {}}>
                      <label style={profileLabel}>{label}</label>
                      <input type={type || 'text'} value={profile[key] || ''} onChange={e => setField(key, e.target.value)} style={profileInput} onFocus={iF} onBlur={iB} />
                    </div>
                  ))}
                  <div>
                    <label style={profileLabel}>Gender</label>
                    <select value={profile.gender || ''} onChange={e => setField('gender', e.target.value)} style={profileInput} onFocus={iF} onBlur={iBS}>
                      <option value="">Select gender…</option>
                      {['Male','Female','Non-binary','Prefer not to say'].map(g => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={profileLabel}>I am a…</label>
                    <select value={profile.userType || ''} onChange={e => setField('userType', e.target.value)} style={profileInput} onFocus={iF} onBlur={iBS}>
                      <option value="">Select type…</option>
                      {['Student','Employed','Job Seeking','Freelancer'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={profileLabel}>Professional Summary</label>
                    <textarea value={profile.profile || ''} onChange={e => setField('profile', e.target.value)} rows={4} placeholder="A short summary that appears at the top of your resume…" style={{ ...profileInput, resize: 'vertical', lineHeight: 1.6 }} onFocus={iF} onBlur={iB} />
                  </div>
                </div>
              </div>
            )}

            {/* ─ Job Preference ─ */}
            {profileSection === 'preference' && (
              <div style={profileCard}>
                <SectionHeading icon={<BriefcaseBusiness size={16} color="#EF1D28" />} title="Job Preference" subtitle="Personalises job matches and AI suggestions" />
                <div style={{ display: 'grid', gap: 18 }}>
                  <div>
                    <label style={profileLabel}>Industry</label>
                    <select value={preferences.industry || ''} onChange={e => setPref('industry', e.target.value)} style={profileInput} onFocus={iF} onBlur={iBS}>
                      <option value="">Select industry…</option>
                      {['Technology','Design','Marketing','Sales','Finance','Healthcare','Education','Operations','Engineering','Legal','Real Estate','Media','Other'].map(i => <option key={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={profileLabel}>Seniority Level</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                      {['Entry','Junior','Mid','Senior','Lead','Manager','Executive'].map(level => (
                        <PillOption key={level} label={level} active={(preferences.seniority || 'Entry') === level} onClick={() => setPref('seniority', level)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={profileLabel}>Area of Residence</label>
                    <select value={preferences.areaOfResidence || ''} onChange={e => setPref('areaOfResidence', e.target.value)} style={profileInput} onFocus={iF} onBlur={iBS}>
                      <option value="">Select…</option>
                      {[profile.city, profile.country, 'Remote', 'Hybrid', 'On-site'].filter(Boolean).map(i => <option key={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={profileLabel}>Employment Type</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                      {['Full-Time','Part-Time','Freelance','Internship','Contract'].map(item => (
                        <PillOption key={item} label={item} active={employmentTypes.includes(item)} onClick={() => toggleEmployment(item)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={profileLabel}>Salary Rate</label>
                    <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                      {['Hourly','Monthly','Yearly'].map(rate => (
                        <PillOption key={rate} label={rate} active={(preferences.salaryRate || 'Yearly') === rate} onClick={() => setPref('salaryRate', rate)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={profileLabel}>Minimum Salary</label>
                    <div style={{ position: 'relative' }}>
                      <input type="number" min="0" value={preferences.minimumSalary || ''} onChange={e => setPref('minimumSalary', e.target.value)} style={{ ...profileInput, paddingRight: 90 }} onFocus={iF} onBlur={iB} />
                      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9CA3AF', pointerEvents: 'none' }}>USD / {(preferences.salaryRate || 'Yearly').toLowerCase()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─ Experience ─ */}
            {profileSection === 'experience' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Work Experience <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>({(profile.experience||[]).length} entries)</span></div>
                  <button type="button" onClick={addExp} style={addBtnStyle}>+ Add Experience</button>
                </div>
                {(profile.experience||[]).length === 0 && <EmptyHint text="No experience added yet. Click '+ Add Experience' to get started." />}
                {(profile.experience||[]).map(exp => (
                  <div key={exp.id} style={entryCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{exp.title || 'New Position'}{exp.company ? ` @ ${exp.company}` : ''}</div>
                      <button type="button" onClick={() => delExp(exp.id)} style={delBtnStyle}>Remove</button>
                    </div>
                    <Row2>
                      <Field label="Job Title" value={exp.title} onChange={v => setExp(exp.id,'title',v)} placeholder="e.g. Software Engineer" />
                      <Field label="Company" value={exp.company} onChange={v => setExp(exp.id,'company',v)} placeholder="e.g. Google" />
                      <Field label="Location" value={exp.location} onChange={v => setExp(exp.id,'location',v)} placeholder="e.g. New York, NY" />
                      <div />
                      <Field label="Start Date" value={exp.startDate} onChange={v => setExp(exp.id,'startDate',v)} type="month" />
                      <Field label="End Date" value={exp.endDate} onChange={v => setExp(exp.id,'endDate',v)} type="month" />
                    </Row2>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#6B7280', cursor: 'pointer', marginBottom: 10 }}>
                      <input type="checkbox" checked={!!exp.current} onChange={e => setExp(exp.id,'current',e.target.checked)} style={{ accentColor: '#EF1D28' }} />
                      Currently working here
                    </label>
                    <div>
                      <label style={profileLabel}>Description</label>
                      <textarea value={exp.description || ''} onChange={e => setExp(exp.id,'description',e.target.value)} rows={3} placeholder="Key responsibilities and achievements…" style={{ ...profileInput, resize: 'vertical', lineHeight: 1.6 }} onFocus={iF} onBlur={iB} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ─ Education ─ */}
            {profileSection === 'education' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Education <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>({(profile.education||[]).length} entries)</span></div>
                  <button type="button" onClick={addEdu} style={addBtnStyle}>+ Add Education</button>
                </div>
                {(profile.education||[]).length === 0 && <EmptyHint text="No education added yet. Click '+ Add Education' to get started." />}
                {(profile.education||[]).map(edu => (
                  <div key={edu.id} style={entryCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{edu.degree || 'New Degree'}{edu.school ? ` — ${edu.school}` : ''}</div>
                      <button type="button" onClick={() => delEdu(edu.id)} style={delBtnStyle}>Remove</button>
                    </div>
                    <Row2>
                      <Field label="Degree / Qualification" value={edu.degree} onChange={v => setEdu(edu.id,'degree',v)} placeholder="e.g. BSc Computer Science" span2 />
                      <Field label="School / University" value={edu.school} onChange={v => setEdu(edu.id,'school',v)} placeholder="e.g. MIT" />
                      <Field label="Location" value={edu.location} onChange={v => setEdu(edu.id,'location',v)} placeholder="e.g. Cambridge, MA" />
                      <Field label="Start Date" value={edu.startDate} onChange={v => setEdu(edu.id,'startDate',v)} type="month" />
                      <Field label="End Date" value={edu.endDate} onChange={v => setEdu(edu.id,'endDate',v)} type="month" />
                      <Field label="GPA (optional)" value={edu.gpa} onChange={v => setEdu(edu.id,'gpa',v)} placeholder="e.g. 3.8" />
                    </Row2>
                  </div>
                ))}
              </div>
            )}

            {/* ─ Skills ─ */}
            {profileSection === 'skills' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Skills <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>({(profile.skills||[]).length} categories)</span></div>
                  <button type="button" onClick={addSkill} style={addBtnStyle}>+ Add Category</button>
                </div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16 }}>Group skills by category (e.g. Languages, Frameworks, Tools). Separate individual skills with commas.</div>
                {(profile.skills||[]).length === 0 && <EmptyHint text="No skills added yet. Click '+ Add Category' to get started." />}
                {(profile.skills||[]).map(skill => (
                  <div key={skill.id} style={entryCard}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ flex: '0 0 160px' }}>
                        <label style={profileLabel}>Category</label>
                        <input value={skill.category || ''} onChange={e => setSkill(skill.id,'category',e.target.value)} placeholder="e.g. Programming Languages" style={profileInput} onFocus={iF} onBlur={iB} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={profileLabel}>Skills (comma-separated)</label>
                        <input value={skill.items || ''} onChange={e => setSkill(skill.id,'items',e.target.value)} placeholder="e.g. JavaScript, Python, TypeScript" style={profileInput} onFocus={iF} onBlur={iB} />
                      </div>
                      <button type="button" onClick={() => delSkill(skill.id)} style={{ ...delBtnStyle, marginTop: 22, flexShrink: 0 }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ─ Courses ─ */}
            {profileSection === 'courses' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Courses <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>({(profile.courses||[]).length} entries)</span></div>
                  <button type="button" onClick={addCourse} style={addBtnStyle}>+ Add Course</button>
                </div>
                {(profile.courses||[]).length === 0 && <EmptyHint text="No courses added yet. Click '+ Add Course' to get started." />}
                {(profile.courses||[]).map(course => (
                  <div key={course.id} style={entryCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{course.name || 'New Course'}{course.provider ? ` — ${course.provider}` : ''}</div>
                      <button type="button" onClick={() => delCourse(course.id)} style={delBtnStyle}>Remove</button>
                    </div>
                    <Row2>
                      <Field label="Course Name" value={course.name} onChange={v => setCourse(course.id,'name',v)} placeholder="e.g. Machine Learning Specialization" span2 />
                      <Field label="Provider / Platform" value={course.provider} onChange={v => setCourse(course.id,'provider',v)} placeholder="e.g. Coursera, Udemy, Google" />
                      <Field label="Completion Date" value={course.date} onChange={v => setCourse(course.id,'date',v)} type="month" />
                    </Row2>
                  </div>
                ))}
              </div>
            )}

            {/* ─ Certificates ─ */}
            {profileSection === 'certificates' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Certificates <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>({(profile.certifications||[]).length} entries)</span></div>
                  <button type="button" onClick={addCert} style={addBtnStyle}>+ Add Certificate</button>
                </div>
                {(profile.certifications||[]).length === 0 && <EmptyHint text="No certificates added yet. Click '+ Add Certificate' to get started." />}
                {(profile.certifications||[]).map(cert => (
                  <div key={cert.id} style={entryCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{cert.name || 'New Certificate'}{cert.issuer ? ` — ${cert.issuer}` : ''}</div>
                      <button type="button" onClick={() => delCert(cert.id)} style={delBtnStyle}>Remove</button>
                    </div>
                    <Row2>
                      <Field label="Certificate Name" value={cert.name} onChange={v => setCert(cert.id,'name',v)} placeholder="e.g. AWS Certified Solutions Architect" span2 />
                      <Field label="Issuing Organisation" value={cert.issuer} onChange={v => setCert(cert.id,'issuer',v)} placeholder="e.g. Amazon Web Services" />
                      <Field label="Date Issued" value={cert.date} onChange={v => setCert(cert.id,'date',v)} type="month" />
                      <Field label="Credential ID (optional)" value={cert.credentialId} onChange={v => setCert(cert.id,'credentialId',v)} placeholder="e.g. ABC-123456" span2 />
                    </Row2>
                  </div>
                ))}
              </div>
            )}
            </div>

            <div className="profile-strength-side">
              <ProfileStrengthCard profile={profile} docs={docs} onSectionClick={handleStrengthClick} />
            </div>
          </div>
        );
      })()}

      {/* ── Account Settings tab ── */}
      {profileTab === 'account' && (
        <div style={{ maxWidth: 580 }}>

          {/* Status + Plan cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
            <div style={{ ...profileCard, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldCheck size={18} color="#059669" />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Account Status</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#059669', textTransform: 'capitalize' }}>{user.status || 'Active'}</div>
              </div>
            </div>
            <div style={{ ...profileCard, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFF1F1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Crown size={18} color="#EF1D28" />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Account Plan</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#EF1D28', textTransform: 'capitalize' }}>{user.plan || 'Free'}</div>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div style={{ ...profileCard, marginBottom: 18 }}>
            <SectionHeading icon={<KeyRound size={16} color="#EF1D28" />} title="Change Password" subtitle="Update your account password" />
            {pwMessage.text && (
              <div style={{ background: pwMessage.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${pwMessage.ok ? '#BBF7D0' : '#FECACA'}`, color: pwMessage.ok ? '#166534' : '#DC2626', padding: '10px 14px', borderRadius: 10, marginBottom: 14, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span>{pwMessage.ok ? '✓' : '!'}</span> {pwMessage.text}
              </div>
            )}
            <div style={{ display: 'grid', gap: 12 }}>
              {[
                [pwForm.currentPassword, 'Current password', 'currentPassword'],
                [pwForm.newPassword, 'New password', 'newPassword'],
                [pwForm.confirmPassword, 'Confirm new password', 'confirmPassword'],
              ].map(([val, placeholder, key]) => (
                <input key={key} type="password" value={val} onChange={e => setPwForm(prev => ({ ...prev, [key]: e.target.value }))} placeholder={placeholder} style={profileInput}
                  onFocus={e => { e.target.style.borderColor = '#EF1D28'; e.target.style.boxShadow = '0 0 0 3px rgba(239,29,40,0.08)'; e.target.style.background = 'white'; }}
                  onBlur={e => { e.target.style.borderColor = '#E8EDF2'; e.target.style.boxShadow = 'none'; e.target.style.background = '#FAFBFC'; }} />
              ))}
              <button type="button" onClick={handlePasswordChange} disabled={pwSaving}
                style={{ background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', border: 'none', borderRadius: 10, padding: '11px 14px', fontSize: 13, fontWeight: 700, cursor: pwSaving ? 'not-allowed' : 'pointer', boxShadow: '0 3px 10px rgba(239,29,40,0.25)', transition: 'opacity 0.15s', opacity: pwSaving ? 0.7 : 1 }}>
                {pwSaving ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </div>

          {/* Delete Account */}
          <div style={{ ...profileCard, border: '1px solid #FEE2E2', background: '#FFFBFB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #FEE2E2' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UserX size={16} color="#DC2626" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626' }}>Delete Account</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>This action is permanent and cannot be undone</div>
              </div>
            </div>
            <p style={{ margin: '0 0 14px', color: '#6B7280', fontSize: 12, lineHeight: 1.6 }}>
              All your documents, resumes, cover letters, and account data will be <strong style={{ color: '#DC2626' }}>permanently deleted</strong>. Please type <strong>DELETE</strong> below to confirm.
            </p>
            <input value={deleteText} onChange={e => setDeleteText(e.target.value)} placeholder='Type "DELETE" to confirm' style={{ ...profileInput, borderColor: deleteText === 'DELETE' ? '#FECACA' : '#E8EDF2', marginBottom: 12 }}
              onFocus={e => { e.target.style.boxShadow = '0 0 0 3px rgba(220,38,38,0.08)'; }}
              onBlur={e => { e.target.style.boxShadow = 'none'; }} />
            <button type="button" onClick={handleDeleteAccount} disabled={deleteText !== 'DELETE' || deleting}
              style={{ width: '100%', background: deleteText === 'DELETE' ? 'linear-gradient(135deg,#DC2626,#EF4444)' : '#FCA5A5', color: 'white', border: 'none', borderRadius: 10, padding: '11px 14px', fontSize: 13, fontWeight: 700, cursor: deleteText === 'DELETE' && !deleting ? 'pointer' : 'not-allowed', transition: 'all 0.2s', boxShadow: deleteText === 'DELETE' ? '0 3px 10px rgba(220,38,38,0.3)' : 'none' }}>
              {deleting ? 'Deleting…' : 'Delete My Account'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FEEDBACK TAB
═══════════════════════════════════════════════════════════════════════════ */
const FeedbackTab = ({ user }) => {
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', phone: '', company: '', rating: 0, message: '' });
  const [status, setStatus] = useState('idle');
  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const token = localStorage.getItem('rb_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${apiBase}/contact/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...form, plan: user?.plan || 'free' }),
      });
      if (!res.ok) throw new Error();
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const inpStyle = { width: '100%', border: '1.5px solid #E8EDF2', borderRadius: 10, background: '#FAFBFC', padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', color: '#1F2937' };
  const iF = e => { e.target.style.borderColor = '#EF1D28'; e.target.style.boxShadow = '0 0 0 3px rgba(239,29,40,0.08)'; e.target.style.background = 'white'; };
  const iB = e => { e.target.style.borderColor = '#E8EDF2'; e.target.style.boxShadow = 'none'; e.target.style.background = '#FAFBFC'; };
  const lbl = { display: 'block', color: '#6B7280', fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' };

  if (status === 'sent') return (
    <div style={{ maxWidth: 540, margin: '60px auto', textAlign: 'center' }}>
      <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 36 }}>✅</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: '#0F172A', marginBottom: 10 }}>Thank you for your feedback!</div>
      <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, marginBottom: 28 }}>Your feedback has been sent to our team. We read every submission and use it to make MarsResume better.</div>
      <button onClick={() => setStatus('idle')} style={{ background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Send More Feedback</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 620, margin: '0 auto' }}>
      <div style={{ background: 'linear-gradient(135deg,#0F172A,#1E293B)', borderRadius: 18, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 80% 50%,rgba(239,29,40,0.18) 0%,transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(239,29,40,0.15)', border: '1px solid rgba(239,29,40,0.25)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: '#FCA5A5', fontWeight: 600, marginBottom: 12, letterSpacing: '0.03em' }}>💬 Share Your Feedback</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 900, color: 'white', letterSpacing: -0.5 }}>How's your experience?</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>Help us improve MarsResume. Your feedback goes directly to the team.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
        {/* Star rating */}
        <div style={{ marginBottom: 24 }}>
          <label style={lbl}>Overall Rating</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button" onClick={() => setForm(f => ({ ...f, rating: n }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 32, lineHeight: 1, transition: 'transform 0.1s', filter: n <= form.rating ? 'none' : 'grayscale(1) opacity(0.3)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}>⭐</button>
            ))}
            {form.rating > 0 && <span style={{ fontSize: 12, color: '#F59E0B', fontWeight: 700 }}>{['','Poor','Fair','Good','Very Good','Excellent!'][form.rating]}</span>}
          </div>
        </div>

        {/* Feedback message */}
        <div style={{ marginBottom: 20 }}>
          <label style={lbl}>Your Experience / Suggestion <span style={{ color: '#EF1D28' }}>*</span></label>
          <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required rows={5}
            placeholder="Share what you love, what's missing, or how we can improve…"
            style={{ ...inpStyle, resize: 'vertical', lineHeight: 1.6 }} onFocus={iF} onBlur={iB} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={lbl}>Name (optional)</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" style={inpStyle} onFocus={iF} onBlur={iB} />
          </div>
          <div>
            <label style={lbl}>Email (optional)</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" style={inpStyle} onFocus={iF} onBlur={iB} />
          </div>
          <div>
            <label style={lbl}>Company / Role (optional)</label>
            <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="e.g. Software Engineer at Google" style={inpStyle} onFocus={iF} onBlur={iB} />
          </div>
          <div>
            <label style={lbl}>Phone (optional)</label>
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 234 567 8900" style={inpStyle} onFocus={iF} onBlur={iB} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Your Plan</label>
            <div style={{ ...inpStyle, background: user?.plan === 'pro' ? '#ECFDF5' : '#FFFBEB', borderColor: user?.plan === 'pro' ? '#A7F3D0' : '#FDE68A', color: user?.plan === 'pro' ? '#059669' : '#B45309', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              {user?.plan === 'pro' ? '⭐ Pro' : '🆓 Free'}
            </div>
          </div>
        </div>

        {status === 'error' && <div style={{ background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#DC2626', marginBottom: 16 }}>Failed to send. Please try again.</div>}

        <button type="submit" disabled={!form.message.trim() || status === 'sending'}
          style={{ width: '100%', background: !form.message.trim() ? '#E5E7EB' : 'linear-gradient(135deg,#EF1D28,#C5161D)', color: !form.message.trim() ? '#9CA3AF' : 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: !form.message.trim() ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: form.message.trim() ? '0 4px 16px rgba(239,29,40,0.3)' : 'none', transition: 'all 0.15s' }}>
          {status === 'sending' ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</> : 'Send Feedback'}
        </button>
      </form>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CONTACT TAB
═══════════════════════════════════════════════════════════════════════════ */
const ContactTab = ({ user }) => {
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');
  const apiBase = process.env.REACT_APP_API_URL || '/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    try {
      const res = await fetch(`${apiBase}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const inpStyle = { width: '100%', border: '1.5px solid #E8EDF2', borderRadius: 10, background: '#FAFBFC', padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', color: '#1F2937' };
  const iF = e => { e.target.style.borderColor = '#EF1D28'; e.target.style.boxShadow = '0 0 0 3px rgba(239,29,40,0.08)'; e.target.style.background = 'white'; };
  const iB = e => { e.target.style.borderColor = '#E8EDF2'; e.target.style.boxShadow = 'none'; e.target.style.background = '#FAFBFC'; };
  const lbl = { display: 'block', color: '#6B7280', fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' };

  if (status === 'sent') return (
    <div style={{ maxWidth: 540, margin: '60px auto', textAlign: 'center' }}>
      <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#D1FAE5,#A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 36 }}>✅</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: '#0F172A', marginBottom: 10 }}>Message sent!</div>
      <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, marginBottom: 28 }}>We've received your message and will reply to <strong>{form.email}</strong> within 24 hours.</div>
      <button onClick={() => { setForm({ name: user?.name || '', email: user?.email || '', subject: '', message: '' }); setStatus('idle'); }}
        style={{ background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Send Another Message</button>
    </div>
  );

  const canSubmit = form.name && form.email && form.message;

  return (
    <div style={{ maxWidth: 620, margin: '0 auto' }}>
      <div style={{ background: 'linear-gradient(135deg,#0F172A,#1E293B)', borderRadius: 18, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 80% 50%,rgba(239,29,40,0.18) 0%,transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(239,29,40,0.15)', border: '1px solid rgba(239,29,40,0.25)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: '#FCA5A5', fontWeight: 600, marginBottom: 12, letterSpacing: '0.03em' }}>📬 Contact Us</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 900, color: 'white', letterSpacing: -0.5 }}>We're here to help</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>Send us a message and we'll get back to you within 24 hours.</p>
        </div>
      </div>

      {/* Quick info row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '📧', label: 'Email Us', value: 'support@marsresume.com', href: 'mailto:support@marsresume.com' },
          { icon: '⏰', label: 'Response Time', value: 'Within 24 hours' },
        ].map(({ icon, label, value, href }) => (
          <div key={label} style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: '#FFF1F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{icon}</div>
            <div>
              <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
              {href ? <a href={href} style={{ fontSize: 12, fontWeight: 700, color: '#EF1D28', textDecoration: 'none' }}>{value}</a>
                    : <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{value}</div>}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 18, padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <label style={lbl}>Name <span style={{ color: '#EF1D28' }}>*</span></label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Your name" style={inpStyle} onFocus={iF} onBlur={iB} />
          </div>
          <div>
            <label style={lbl}>Email <span style={{ color: '#EF1D28' }}>*</span></label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="you@example.com" style={inpStyle} onFocus={iF} onBlur={iB} />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Subject</label>
          <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="What's this about?" style={inpStyle} onFocus={iF} onBlur={iB} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={lbl}>Message <span style={{ color: '#EF1D28' }}>*</span></label>
          <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required rows={5}
            placeholder="Describe how we can help you…" style={{ ...inpStyle, resize: 'vertical', lineHeight: 1.6 }} onFocus={iF} onBlur={iB} />
        </div>
        {status === 'error' && <div style={{ background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#DC2626', marginBottom: 16 }}>Failed to send. Please try again.</div>}
        <button type="submit" disabled={!canSubmit || status === 'sending'}
          style={{ width: '100%', background: !canSubmit ? '#E5E7EB' : 'linear-gradient(135deg,#EF1D28,#C5161D)', color: !canSubmit ? '#9CA3AF' : 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: !canSubmit ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: canSubmit ? '0 4px 16px rgba(239,29,40,0.3)' : 'none', transition: 'all 0.15s' }}>
          {status === 'sending' ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</> : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const DashboardPageLegacy = () => {
  const { user, logout, updateProfile } = useAuth();
  const { showAlert, showConfirm } = useDialog();
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const navigate = useNavigate();

  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.getAnnouncements().then(data => {
      setAnnouncements(data.filter(a => 
        a.targetAudience === 'all' || a.targetAudience === user.plan
      ));
    }).catch(() => {});
  }, []);


  useEffect(() => {
    api.getDocs()
      .then(setDocs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    const ok = await showConfirm('Are you sure you want to delete this document? This cannot be undone.', {
      title: 'Delete Document', confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    await api.deleteDoc(id);
    setDocs(docs.filter(d => d._id !== id));
  };

  const handleCreate = async (type) => {
    navigate(`/builder/${type}/new`);
  };

  const handleUseTemplate = async (templateId, type = 'resume') => {
    navigate(`/builder/${type}/new?template=${templateId}`);
  };

  const fmtTime = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const tabBtn = (id, label, icon) => (
    <button onClick={() => setActiveTab(id)} style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
      background: activeTab === id ? 'white' : 'transparent',
      color: activeTab === id ? '#C5161D' : '#6B7280',
      border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13,
      cursor: 'pointer', boxShadow: activeTab === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
      transition: 'all 0.15s',
    }}>
      <span>{icon}</span>{label}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .dash-nav { display: flex; align-items: center; justify-content: space-between; background: white; border-bottom: 1px solid #E5E7EB; padding: 0 32px; height: 62px; position: sticky; top: 0; z-index: 50; box-shadow: 0 1px 4px rgba(0,0,0,0.04); gap: 12px; }
        .dash-logo-text { display: block; }
        .dash-tabs { display: flex; background: #F1F5F9; border-radius: 10px; padding: 3px; gap: 2px; flex-shrink: 1; overflow-x: auto; max-width: 100%; }
        .dash-user-name { display: block; }
        .dash-user-email { display: block; }
        .dash-badge { display: inline-block; }
        .dash-logout-text { display: inline; }
        @media (max-width: 860px) {
          .dash-nav { padding: 0 16px; height: auto; min-height: 56px; flex-wrap: wrap; padding-top: 8px; padding-bottom: 8px; gap: 8px; }
          .dash-logo-text { display: none; }
          .dash-badge { display: none; }
          .dash-user-name { display: none; }
          .dash-user-email { display: none; }
          .dash-tabs { order: 3; width: 100%; justify-content: center; }
        }
        @media (max-width: 480px) {
          .dash-logout-text { display: none; }
        }
      `}</style>

      {/* ── Navbar ── */}
      <nav className="dash-nav">
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexShrink: 0 }} onClick={() => navigate('/')}>
          <img src="/assets/mars-logo.png" alt="MarsResume logo" style={{ width: 36, height: 36, objectFit: 'contain', display: 'block' }} />
          <div className="dash-logo-text">
            <span style={{ fontSize: 18, fontWeight: 900, background: 'linear-gradient(135deg,#C5161D,#FB923C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>MarsResume</span>
            <span style={{ fontSize: 9, display: 'block', color: '#9CA3AF', marginTop: -2 }}>Resume That Takes You to Mars</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="dash-tabs">
          {tabBtn('home',      'Home',      '🏠')}
          {tabBtn('documents', 'Documents', '📁')}
          {tabBtn('templates', 'Templates', '🎨')}
          {tabBtn('referral',  'Refer',     '🎁')}
          {tabBtn('profile',   'Profile',   'ID')}
        </div>

        {/* Right — user info + logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span className="dash-badge" style={{ background: '#ECFDF5', color: '#059669', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✓ All Features</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {user.avatar
              ? <img src={user.avatar} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '2px solid #E5E7EB' }} />
              : <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#C5161D,#FB923C)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 14 }}>{user.name?.charAt(0).toUpperCase()}</div>}
            <div>
              <div className="dash-user-name" style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>{user.name}</div>
              <div className="dash-user-email" style={{ fontSize: 10, color: '#9CA3AF' }}>{user.email}</div>
            </div>
          </div>
          <button onClick={logout} style={{ background: '#FFF1F2', border: '1.5px solid #FECDD3', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#EF1D28', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#EF1D28'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#EF1D28'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#FFF1F2'; e.currentTarget.style.color = '#EF1D28'; e.currentTarget.style.borderColor = '#FECDD3'; }}>
            <LogOut size={13} /><span className="dash-logout-text">Logout</span>
          </button>
        </div>
      </nav>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 28px 60px' }}>

        {/* ══════════ HOME TAB ══════════ */}
        {activeTab === 'home' && (
          <>
          {announcements.map(ann => {
              const colors = {
                info:    { bg: '#FFF1E8', border: '#FDBA74', text: '#991B1B', icon: 'ℹ️' },
                warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', icon: '⚠️' },
                success: { bg: '#F0FDF4', border: '#BBF7D0', text: '#065F46', icon: '✅' },
                error:   { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', icon: '❌' },
              };
              const c = colors[ann.type] || colors.info;
              return (
                <div key={ann._id} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '12px 16px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{c.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: c.text, fontSize: 14 }}>{ann.title}</div>
                    <div style={{ color: c.text, fontSize: 13, opacity: 0.85, marginTop: 2 }}>{ann.message}</div>
                  </div>
                </div>
              );
            })}
            {/* Welcome Banner */}
            <div style={{ background: 'linear-gradient(135deg,#7F1D1D,#F97316,#FB923C)', borderRadius: 18, padding: '32px 36px', marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', overflow: 'hidden', position: 'relative' }}>
              <div style={{ position: 'absolute', top: -30, right: 200, width: 200, height: 200, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
              <div style={{ position: 'absolute', bottom: -40, right: 60, width: 160, height: 160, background: 'rgba(255,255,255,0.04)', borderRadius: '50%' }} />
              <div style={{ position: 'relative' }}>
                <h1 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: 'white' }}>
                  Welcome back, {user.name?.split(' ')[0]}! 👋
                </h1>
                <p style={{ margin: '0 0 20px', color: '#FDBA74', fontSize: 14 }}>
                  Create AI-powered professional documents in minutes.
                </p>
                <button onClick={() => navigate('/builder/resume/ai')} style={{ background: 'white', color: '#F97316', border: 'none', borderRadius: 10, padding: '10px 22px', fontWeight: 800, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
                  ✨ Generate with AI
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, position: 'relative' }}>
                {[
                  { label: 'Documents', value: docs.length },
                  { label: 'Plan', value: user.plan === 'pro' ? '⭐ Pro' : '🆓 Free' },
                  { label: 'Templates', value: '9' },
                  { label: 'AI Builds', value: '∞' },
                ].map((s, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '12px 16px', textAlign: 'center', backdropFilter: 'blur(8px)' }}>
                    <div style={{ color: 'white', fontWeight: 800, fontSize: 20 }}>{s.value}</div>
                    <div style={{ color: '#FDBA74', fontSize: 11, marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Create New Document */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h2 style={{ margin: '0 0 3px', fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Create New Document</h2>
                  <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>Start from scratch or let AI build it for you</p>
                </div>
              </div>
              <div className="dash-create-grid">
                {DOC_TYPES.map(dt => (
                  <div key={dt.type} style={{ background: 'white', border: '2px solid #F1F5F9', borderRadius: 16, padding: 22, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                    onClick={() => handleCreate(dt.type)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = dt.color; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${dt.color}22`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#F1F5F9'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}>
                    <div style={{ width: 48, height: 48, background: dt.bg, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 14 }}>{dt.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#0F172A', marginBottom: 5 }}>{dt.label}</div>
                    <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5, marginBottom: 14 }}>{dt.desc}</div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: dt.color, fontSize: 12, fontWeight: 700 }}>
                      Create {dt.label} →
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </>
        )}

        {/* ══════════ DOCUMENTS TAB ══════════ */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div>
                <h2 style={{ margin: '0 0 3px', fontSize: 20, fontWeight: 800, color: '#0F172A' }}>My Documents ({docs.length})</h2>
                <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>Unlimited documents · All features included</p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {DOC_TYPES.map(dt => (
                  <button key={dt.type} onClick={() => handleCreate(dt.type)}
                    style={{ background: dt.gradient, color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {dt.icon} {dt.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <RocketLoader size={160} message="Loading documents…" />
              </div>
            ) : docs.length === 0 ? (
              <div style={{ background: 'white', border: '2px dashed #E2E8F0', borderRadius: 16, padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 14 }}>📋</div>
                <div style={{ fontWeight: 700, color: '#374151', fontSize: 16, marginBottom: 6 }}>No documents yet</div>
                <div style={{ color: '#94A3B8', fontSize: 13, marginBottom: 20 }}>Create your first resume, cover letter, or CV</div>
                <button onClick={() => handleCreate('resume')} style={{ background: 'linear-gradient(135deg,#C5161D,#FB923C)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}>
                  + Create Resume
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: 16 }}>
                {docs.map(doc => (
                  <div key={doc._id} style={{ background: 'white', border: '1px solid #F1F5F9', borderRadius: 16, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ fontSize: 28 }}>{typeIcon[doc.type] || '📄'}</div>
                      <span style={{ fontSize: 11, background: '#F8FAFC', color: '#64748B', padding: '3px 9px', borderRadius: 20, fontWeight: 600, border: '1px solid #E2E8F0' }}>{typeLabel[doc.type]}</span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#0F172A', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.data?.personalInfo?.fullName || doc.title || 'Untitled'}
                    </div>
                    <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 4 }}>
                      {doc.data?.personalInfo?.jobTitle || doc.data?.jobInfo?.role || 'No title'}
                    </div>
                    <div style={{ fontSize: 11, color: '#CBD5E1', marginBottom: 16 }}>
                      Template: {doc.template || 'modern'} · {fmtTime(doc.updatedAt || doc.createdAt)}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => navigate(`/builder/${doc.type}/${doc._id}`)} style={{ flex: 1, background: '#FFF1E8', color: '#EF1D28', border: 'none', borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✏️ Edit</button>
                      <button onClick={() => handleDelete(doc._id)} style={{ background: '#FEF2F2', color: '#EF4444', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════ TEMPLATES TAB ══════════ */}
        {activeTab === 'templates' && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#0F172A' }}>Choose a Template</h2>
              <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>Pick a template and start building — you can change it anytime in the editor</p>
            </div>

            {/* Free Templates */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Free Templates</h3>
                <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Available Now</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16 }}>
                {TEMPLATES.filter(t => t.free).map(tmpl => (
                  <TemplateCard key={tmpl.id} tmpl={tmpl} onUse={handleUseTemplate} locked={false} />
                ))}
              </div>
            </div>

            {/* More Templates */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A' }}>More Templates</h3>
                {user.plan !== 'pro' && (
                  <span style={{ background: '#FFEDD5', color: '#EA580C', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>⭐ Pro Plan</span>
                )}
                {user.plan === 'pro' && (
                  <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>✓ Unlocked</span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16 }}>
                {TEMPLATES.filter(t => !t.free).map(tmpl => (
                  <TemplateCard key={tmpl.id} tmpl={tmpl} onUse={handleUseTemplate} locked={false} onUpgrade={() => navigate('/pricing')} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'referral' && (
          <ReferralTab user={user} />
        )}

        {activeTab === 'profile' && (
          <ProfileTab user={user} onSave={updateProfile} docs={docs} onNavigateResume={() => setActiveTab('documents')} />
        )}

      </div>
    </div>
  );
};

// ── Template Card Component ────────────────────────────────────────────────────
const DASHBOARD_DOC_TABS = [
  { id: 'resume', type: 'resume', label: 'Resumes', singular: 'Resume', icon: FileText, color: '#EF1D28' },
  { id: 'cover_letter', type: 'cover_letter', label: 'Cover Letters', singular: 'Cover Letter', icon: Mail, color: '#059669' },
  { id: 'cv', type: 'cv', label: 'Academic CV', singular: 'Academic CV', icon: GraduationCap, color: '#FB923C' },
];

const TEMPLATE_EXAMPLES = {
  modern: { title: 'Property Manager CV Example', hiredBy: 'AAA Apartment Staffing', name: 'Sarah Chen', role: 'Property Manager' },
  classic: { title: 'Assembler Resume Example', hiredBy: 'Able Industries', name: 'Sarah Chen', role: 'Assembler' },
  executive: { title: 'Human Resources Intern Resume', hiredBy: 'ACACY', name: 'Sarah Chen', role: 'Human Resources Intern' },
  minimal: { title: 'Researcher CV Example', hiredBy: 'Central European Institute', name: 'Sarah Chen', role: 'Doctoral Researcher' },
  creative: { title: 'Designer Resume Example', hiredBy: 'Tancy Studio', name: 'Sarah Chen', role: 'Product Designer' },
  tech: { title: 'Software Engineer Resume', hiredBy: 'Devops Markaz Tech', name: 'Sarah Chen', role: 'Software Engineer' },
  elegant: { title: 'Marketing Manager Resume', hiredBy: 'Northstar Media', name: 'Sarah Chen', role: 'Marketing Manager' },
  corporate: { title: 'Business Analyst Resume', hiredBy: 'Global Finance Group', name: 'Sarah Chen', role: 'Business Analyst' },
  navy_timeline: { title: 'Navy Timeline Resume Example', hiredBy: 'MarsResume', name: 'Sarah Chen', role: 'Sales Representative' },
  academic: { title: 'Academic CV Example', hiredBy: 'University Research Lab', name: 'Sarah Chen', role: 'Research Fellow' },
  europass: { title: 'Europass Academic CV', hiredBy: 'MarsResume', name: 'Sarah Chen', role: 'Senior Software Engineer' },
  academic_modern: { title: 'Academic Modern Resume Example', hiredBy: 'MarsResume', name: 'Sarah Chen', role: 'Associate Professor' },
  editorial: { title: 'Content Strategist Resume Example', hiredBy: 'The Print House', name: 'Sarah Chen', role: 'Content Strategist' },
  sage: { title: 'HR Manager Resume Example', hiredBy: 'GreenLeaf Group', name: 'Sarah Chen', role: 'HR Manager' },
  graphite: { title: 'Growth Marketer Resume Example', hiredBy: 'Sparkline Labs', name: 'Sarah Chen', role: 'Growth Marketer' },
};

const buildTemplateExample = (tmpl) => {
  const example = TEMPLATE_EXAMPLES[tmpl.id] || {};
  return {
    ...SAMPLE_RESUME,
    type: 'resume',
    template: tmpl.id,
    personalInfo: {
      ...SAMPLE_RESUME.personalInfo,
      fullName: example.name || SAMPLE_RESUME.personalInfo.fullName,
      jobTitle: example.role || SAMPLE_RESUME.personalInfo.jobTitle,
      photo: null,
      accentColor: '',
      nameColor: '',
      sectionColor: '',
      bodyColor: '',
      sidebarColor: '',
      linkColor: '',
    },
  };
};

const createExampleDocumentData = (type) => {
  if (type === 'cover_letter') {
    return JSON.parse(JSON.stringify({ ...SAMPLE_CL, template: 'cl_classic' }));
  }
  if (type === 'cv') {
    return JSON.parse(JSON.stringify({ ...SAMPLE_RESUME, type: 'cv', template: 'academic', publications: [] }));
  }
  return JSON.parse(JSON.stringify({ ...SAMPLE_RESUME, type: 'resume', template: 'harvard_standard' }));
};

const DashboardResponsiveStyles = () => (
  <style>{`
    .client-dashboard-shell { min-height: 100vh; background: #F2F5F9; color: #111827; font-family: 'Inter', sans-serif; }
    .client-dashboard-sidebar { position: fixed; inset: 0 auto 0 0; width: 264px; background: #FFFFFF; border-right: 1px solid #E8ECF2; display: flex; flex-direction: column; z-index: 80; transition: width 0.22s ease, transform 0.22s ease; box-shadow: 4px 0 24px rgba(0,0,0,0.05); }
    .client-dashboard-shell.sidebar-collapsed .client-dashboard-sidebar { width: 72px; }
    .client-dashboard-main { margin-left: 264px; min-height: 100vh; transition: margin-left 0.22s ease; }
    .client-dashboard-shell.sidebar-collapsed .client-dashboard-main { margin-left: 72px; }
    .client-dashboard-topbar { position: sticky; top: 0; z-index: 95; background: #FFFFFF; border-bottom: 1px solid #E8ECF2; padding: 12px 44px; display: flex; align-items: center; justify-content: flex-end; }
    .client-dashboard-workspace { padding: 36px 44px 60px; }
    .client-dashboard-mobile-menu { display: none; position: fixed; top: 14px; left: 14px; z-index: 120; }
    .client-dashboard-overlay { display: none; }
    .client-dashboard-card-grid { display: grid; grid-template-columns: minmax(260px, 330px) repeat(auto-fill, minmax(260px, 330px)); gap: 24px; align-items: stretch; }
    .client-dashboard-template-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 20px; }
    .dash-sidebar-nav-item { width: 100%; display: flex; align-items: center; gap: 11px; border: none; border-radius: 12px; background: transparent; color: #64748B; padding: 9px 12px; font-size: 13.5px; font-weight: 500; text-align: left; cursor: pointer; transition: all 0.18s; margin-bottom: 2px; font-family: 'Inter', sans-serif; }
    .dash-sidebar-nav-item:hover { background: #F1F5F9; color: #1E293B; }
    .dash-sidebar-nav-item.active { background: #FFF1F2; color: #C5161D; font-weight: 700; border-left: 3px solid #EF1D28; padding-left: 9px; }
    .dash-sidebar-nav-item.active:hover { transform: none; }
    .dash-recent-doc-btn { width: 100%; display: flex; align-items: center; gap: 10px; border: none; border-radius: 10px; background: transparent; padding: 8px 10px; cursor: pointer; text-align: left; font-family: 'Inter', sans-serif; margin-bottom: 2px; transition: background 0.15s; }
    .dash-recent-doc-btn:hover { background: #F5F7FB; }
    .dash-doc-row { display: flex; align-items: center; gap: 12px; padding: 13px 20px; border-bottom: 1px solid #F3F6F9; cursor: pointer; transition: background 0.12s; }
    .dash-doc-row:hover { background: #FAFBFD; }
    .dash-feature-btn { background: white; border: 1px solid #E8ECF2; border-radius: 14px; padding: 20px; cursor: pointer; text-align: left; transition: all 0.2s; font-family: 'Inter', sans-serif; }
    .dash-feature-btn:hover { border-color: #C8D0DC; box-shadow: 0 6px 20px rgba(0,0,0,0.08); transform: translateY(-3px); }
    /* Create doc grid — 3 cols default, responsive via .dash-create-grid class (injected by BuilderPage style tag) */
    .dash-create-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; }
    @media (max-width: 1180px) {
      .client-dashboard-topbar { padding: 12px 28px; }
      .client-dashboard-workspace { padding: 28px 28px 48px; }
      .client-dashboard-card-grid { grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); }
      .dash-create-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 1024px) {
      .dash-home-bottom { grid-template-columns: 1fr !important; }
      .dash-home-features { grid-template-columns: repeat(2, 1fr) !important; }
      .dash-home-stats { grid-template-columns: 1fr 1fr !important; }
    }
    @media (max-width: 900px) {
      .client-dashboard-sidebar,
      .client-dashboard-shell.sidebar-collapsed .client-dashboard-sidebar { width: 264px; transform: translateX(-104%); }
      .client-dashboard-sidebar.open { transform: translateX(0); }
      .client-dashboard-overlay { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.38); z-index: 70; }
      .client-dashboard-main,
      .client-dashboard-shell.sidebar-collapsed .client-dashboard-main { margin-left: 0; }
      .client-dashboard-topbar { padding: 12px 16px; }
      .client-dashboard-workspace { padding: 24px 16px 28px; }
      .client-dashboard-mobile-menu { display: inline-flex; }
      .dash-home-stats { grid-template-columns: 1fr !important; }
      .client-dashboard-template-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; }
    }
    @media (max-width: 640px) {
      .client-dashboard-topbar { padding: 10px 12px !important; }
      .dash-topbar-name { display: none; }
      .client-dashboard-workspace { padding: 20px 12px 24px !important; }
      .client-dashboard-card-grid { grid-template-columns: 1fr !important; gap: 14px !important; }
      .dash-create-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
      .client-dashboard-template-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; gap: 10px !important; }
      .dash-home-features { grid-template-columns: 1fr !important; }
      .dash-home-stats { grid-template-columns: 1fr !important; }
      .dash-doc-row { padding: 10px 12px; }
    }
  `}</style>
);

const dashboardIconButton = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  background: 'white',
  color: '#6B7280',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const fmtDocDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const DashboardDocumentCard = ({ doc, onEdit, onDelete, onDownloadPDF, downloading = false }) => {
  const [hovered, setHovered] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const title = doc.data?.personalInfo?.fullName || doc.title || 'Untitled';
  const subtitle = doc.data?.personalInfo?.jobTitle || doc.data?.jobInfo?.role || '';
  const accent = doc.type === 'cover_letter' ? '#059669' : doc.type === 'cv' ? '#FB923C' : '#EF1D28';
  const dateStr = fmtDocDate(doc.updatedAt || doc.createdAt);
  const previewId = `dashboard-preview-${doc._id}`;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: '#FFFFFF', border: `1px solid ${hovered ? '#D0D5DD' : '#EAECF0'}`, borderRadius: 16, overflow: 'visible', boxShadow: hovered ? '0 12px 32px rgba(0,0,0,0.11)' : '0 1px 4px rgba(0,0,0,0.05)', transition: 'all 0.18s', transform: hovered ? 'translateY(-3px)' : 'none' }}>

      {/* Preview */}
      <div style={{ position: 'relative', height: 300, overflow: 'hidden', background: '#F8FAFC', cursor: 'pointer', borderTopLeftRadius: 16, borderTopRightRadius: 16 }} onClick={onEdit}>
        <div style={{ width: 700, position: 'absolute', top: 0, left: '50%', marginLeft: -350, transform: 'scale(0.44)', transformOrigin: 'top center', pointerEvents: 'none', userSelect: 'none' }}>
          <ResumePreview data={doc.data} id={previewId} />
        </div>
        {/* fade out bottom of preview */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(to bottom, transparent, #F8FAFC)', pointerEvents: 'none' }} />
        {hovered && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,18,35,0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(1px)' }}>
            <div style={{ background: 'white', color: '#111827', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
              <Pencil size={13} /> Edit Document
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 16px 15px', borderTop: '1px solid #F1F3F5' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
              <span style={{ display: 'inline-block', background: `${accent}14`, color: accent, borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', border: `1px solid ${accent}22` }}>
                {typeLabel[doc.type]}
              </span>
              {dateStr && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#C0C9D8' }}>
                  <Clock size={9} /> {dateStr}
                </span>
              )}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <div style={{ display: 'flex', gap: 5, flexShrink: 0, marginTop: 1 }}>
            <button type="button" onClick={onEdit} title="Edit" style={{ ...dashboardIconButton, width: 30, height: 30, borderRadius: 7 }}>
              <Pencil size={13} />
            </button>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setDownloadOpen(prev => !prev); }}
                disabled={downloading}
                title="Download"
                style={{ ...dashboardIconButton, width: 30, height: 30, borderRadius: 7, cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.72 : 1 }}
              >
                {downloading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
              </button>
              {downloadOpen && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', overflow: 'hidden', minWidth: 158, zIndex: 40 }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setDownloadOpen(false); onDownloadPDF(); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', background: 'transparent', border: 'none', fontSize: 13, color: '#111827', cursor: 'pointer', fontFamily: 'Inter, sans-serif', textAlign: 'left', fontWeight: 500 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <FileText size={14} color="#EF1D28" /> PDF
                  </button>
                </div>
              )}
            </div>
            <button type="button" onClick={onDelete} title="Delete" style={{ ...dashboardIconButton, width: 30, height: 30, borderRadius: 7, color: '#EF4444', borderColor: '#FEE2E2', background: '#FFF8F8' }}>
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateActionsPanel = ({ docType, onCreate, onCreateAI, onUseExample, onUpload, creatingExample = false }) => {
  const actions = [
    { label: `New ${docType.singular}`, desc: 'Start from a blank document', icon: FilePlus2, onClick: onCreate, primary: true },
    { label: 'Upload Resume (PDF)', desc: 'Import your existing resume with AI', icon: Upload, onClick: onUpload, primary: false },
    { label: 'Build with AI', desc: 'Let AI generate your content', icon: Sparkles, onClick: onCreateAI, primary: false },
    { label: creatingExample ? 'Creating Example...' : 'Use an Example', desc: 'Start from a pre-filled template', icon: ClipboardList, onClick: onUseExample, primary: false, disabled: creatingExample },
  ];

  return (
    <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      {/* Colored header strip */}
      <div style={{ background: `linear-gradient(135deg, ${docType.color}18 0%, ${docType.color}08 100%)`, borderBottom: `1px solid ${docType.color}20`, padding: '18px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: docType.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Plus size={16} color="white" strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Create {docType.singular}</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>Choose how you'd like to get started</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '16px 16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <React.Fragment key={action.label}>
              <button type="button" onClick={action.onClick} disabled={action.disabled}
                style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 15px', border: action.primary ? `1.5px solid ${docType.color}` : '1px solid #EAECF0', borderRadius: 11, background: action.primary ? `${docType.color}0d` : '#FAFAFA', cursor: action.disabled ? 'not-allowed' : 'pointer', textAlign: 'left', fontFamily: 'Inter, sans-serif', transition: 'all 0.12s', opacity: action.disabled ? 0.72 : 1 }}
                onMouseEnter={e => { if (action.disabled) return; e.currentTarget.style.borderColor = docType.color; e.currentTarget.style.background = `${docType.color}0d`; e.currentTarget.style.transform = 'translateX(2px)'; }}
                onMouseLeave={e => { if (action.disabled) return; e.currentTarget.style.borderColor = action.primary ? docType.color : '#EAECF0'; e.currentTarget.style.background = action.primary ? `${docType.color}0d` : '#FAFAFA'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: action.primary ? docType.color : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={17} color={action.primary ? 'white' : '#6B7280'} strokeWidth={2} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{action.label}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{action.desc}</div>
                </div>
              </button>
              {index === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, height: 1, background: '#F1F3F5' }} />
                  <span style={{ fontSize: 11, color: '#D1D5DB', fontFamily: 'Inter, sans-serif' }}>or</span>
                  <div style={{ flex: 1, height: 1, background: '#F1F3F5' }} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const AIBuildModal = ({ docType, onClose, onStart }) => {
  const [description, setDescription] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const inputStyle = {
    width: '100%',
    border: '1.5px solid #E5E7EB',
    borderRadius: 10,
    padding: '11px 13px',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#374151',
  };
  const canStart = description.trim().length > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 18, width: '100%', maxWidth: 560, boxShadow: '0 24px 70px rgba(0,0,0,0.22)', overflow: 'hidden', fontFamily: 'Inter, sans-serif' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 24px', borderBottom: '1px solid #F1F3F5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Sparkles size={19} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Build {docType.singular} with AI</div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>Create a draft from your background and target job.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ ...dashboardIconButton, width: 32, height: 32, borderRadius: 8 }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: '22px 24px 24px' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 7 }}>
            Your background <span style={{ color: '#EF1D28' }}>*</span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={5}
            placeholder="Example: 5 years as a software engineer, React/Node.js/AWS, led checkout migration, looking for senior frontend roles..."
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6, marginBottom: 16 }}
            onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 7 }}>
            <Link2 size={13} color="#6B7280" /> Job URL for targeting
          </label>
          <input
            value={jobUrl}
            onChange={e => setJobUrl(e.target.value)}
            placeholder="https://careers.example.com/software-engineer-123"
            style={inputStyle}
            onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
          />
          <div style={{ marginTop: 9, fontSize: 11, color: '#9CA3AF', lineHeight: 1.5 }}>
            Paste a job URL to make the AI draft more specific to that role. Add the full job description in the next step if the page is not accessible.
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '12px 18px', border: '1.5px solid #E5E7EB', borderRadius: 10, background: 'white', color: '#374151', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onStart({ description: description.trim(), jobUrl: jobUrl.trim() })}
              disabled={!canStart}
              style={{ flex: 1, padding: '12px 18px', border: 'none', borderRadius: 10, background: canStart ? 'linear-gradient(135deg,#EF1D28,#FB923C)' : '#F3F4F6', color: canStart ? 'white' : '#9CA3AF', fontSize: 14, fontWeight: 800, cursor: canStart ? 'pointer' : 'not-allowed', fontFamily: 'inherit', boxShadow: canStart ? '0 4px 14px rgba(239,29,40,0.30)' : 'none' }}
            >
              Continue to AI Builder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PremiumPanel = ({ onUpgrade }) => (
  <div style={{ minHeight: 464, borderRadius: 18, background: 'linear-gradient(165deg,#C5161D 0%,#EF1D28 48%,#FB923C 100%)', boxShadow: '0 10px 18px rgba(239,29,40,0.22)', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', textAlign: 'center', padding: '40px 34px' }}>
    <div>
      <Crown size={34} style={{ marginBottom: 14 }} />
      <div style={{ fontSize: 30, lineHeight: 1.2, fontWeight: 900 }}>Create stronger<br />documents with <span style={{ fontStyle: 'italic', background: 'white', color: '#C5161D', borderRadius: 5, padding: '0 6px' }}>PRO</span></div>
      <p style={{ margin: '54px 0 0', fontSize: 18, lineHeight: 1.45 }}>Unlock more AI tools and<br />unlimited documents, from</p>
    </div>
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 7, marginBottom: 58 }}>
        <span style={{ fontSize: 16 }}>USD</span>
        <span style={{ fontSize: 54, lineHeight: 1, fontWeight: 300 }}>19</span>
        <span style={{ fontSize: 18 }}>/ month</span>
      </div>
      <button type="button" onClick={onUpgrade} style={{ background: 'white', color: '#C5161D', border: 'none', borderRadius: 11, padding: '12px 24px', fontSize: 17, fontWeight: 800, cursor: 'pointer' }}>
        Upgrade to Pro
      </button>
    </div>
  </div>
);

const AnnouncementList = ({ announcements }) => {
  if (!announcements.length) return null;
  const colors = {
    info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', text: '#047857' },
    error: { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C' },
  };

  return (
    <div>
      {announcements.map((ann) => {
        const c = colors[ann.type] || colors.info;
        return (
          <div key={ann._id} style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
            <strong>{ann.title}</strong>
            <span style={{ marginLeft: 8 }}>{ann.message}</span>
          </div>
        );
      })}
    </div>
  );
};

export const DashboardPage = () => {
  const { user, logout, updateProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showAlert, showConfirm } = useDialog();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeView = searchParams.get('view') || 'dashboard';
  const [announcements, setAnnouncements] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [templateDocType, setTemplateDocType] = useState('resume');
  const [pendingInterview, setPendingInterview] = useState(null);
  const [dashJobs, setDashJobs] = useState([]);
  const [dashJobsLoading, setDashJobsLoading] = useState(false);
  const [dashInterviews, setDashInterviews] = useState([]);
  const [creatingExample, setCreatingExample] = useState(false);
  const [premiumSections, setPremiumSections] = useState([]);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadUpgrade, setDownloadUpgrade] = useState(null);
  const [aiBuildType, setAiBuildType] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileSubTab, setProfileSubTab] = useState('profile');
  const [profileNavNonce, setProfileNavNonce] = useState(0);
  const [profileSectionLink, setProfileSectionLink] = useState('personal');
  const [hiddenNavItems, setHiddenNavItems] = useState([]);

  useEffect(() => {
    api.getPlatformFlags().then(f => setHiddenNavItems(f.hiddenNavItems || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.getAnnouncements()
      .then(data => setAnnouncements(data.filter(a => a.targetAudience === 'all' || a.targetAudience === user.plan)))
      .catch(() => {});
  }, [user.plan]);

  useEffect(() => {
    api.getDocs()
      .then(setDocs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.getInterviews().then(data => setDashInterviews(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.getSectionConfig()
      .then(data => setPremiumSections(data.premiumSections || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get('upgrade') !== 'success') return;
    showAlert('Welcome to Pro! Your account is being upgraded — this may take a moment.', {
      type: 'success', title: 'Upgrade Successful',
    });
    setTimeout(() => refreshUser().catch(() => {}), 2000);
    searchParams.delete('upgrade');
    setSearchParams(searchParams, { replace: true });
  }, []);

  const profile = normalizeUserProfile(user.userProfile, user);
  const displayName = getProfileFullName(profile, user) || user.name || 'My Account';
  const avatar = profile.photo || user.avatar;
  const isOverviewView = activeView === 'dashboard';
  const isDocumentView = isOverviewView || DASHBOARD_DOC_TABS.some(tab => tab.id === activeView);
  const currentDocType = DASHBOARD_DOC_TABS.find(tab => tab.id === activeView) || DASHBOARD_DOC_TABS[0];
  const filteredDocs = isOverviewView ? docs : docs.filter(doc => doc.type === currentDocType.type);
  const docCount = Object.fromEntries(DASHBOARD_DOC_TABS.map(tab => [tab.type, docs.filter(doc => doc.type === tab.type).length]));

  const closeSidebar = () => setSidebarOpen(false);
  const PRO_ONLY_VIEWS = ['interviews', 'job_tracker'];

  /* setView() gates these on click, but a pasted or bookmarked ?view= URL
     bypasses it and would render the Pro view for a free account. Guard the
     resolved view itself so both routes land on the plan page. */
  useEffect(() => {
    if (PRO_ONLY_VIEWS.includes(activeView) && user?.plan !== 'pro') {
      setSearchParams({ view: 'subscription' }, { replace: true });
    }
  }, [activeView, user?.plan]);

  const handleManageBilling = async () => {
    try {
      await openBillingPortal();
    } catch (e) {
      showAlert(e.message || 'Could not open billing portal. Please try again.', {
        type: 'error', title: 'Billing Portal Error',
      });
    }
  };

  const setView = (view) => {
    if (PRO_ONLY_VIEWS.includes(view) && user?.plan !== 'pro') {
      setSearchParams({ view: 'subscription' }, { replace: true });
      closeSidebar();
      return;
    }
    setSearchParams(view === 'dashboard' ? {} : { view }, { replace: true });
    closeSidebar();
  };

  const goToProfileSection = (sectionId) => {
    if (sectionId === 'resume') { setView('resume'); return; }
    const map = { basic: 'personal', summary: 'personal', skills: 'skills', experience: 'experience', education: 'education' };
    setProfileSectionLink(map[sectionId] || 'personal');
    setProfileSubTab('profile');
    setProfileNavNonce((n) => n + 1);
    setView('profile');
  };

  const uploadInputRef = useRef(null);

  const handleUploadResume = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { alert('Please select a PDF file.'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('File must be under 5 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      localStorage.setItem('mars_pending_file', reader.result);
      localStorage.setItem('mars_pending_filename', file.name);
      window.dispatchEvent(new CustomEvent('mars_trigger_import'));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const handleCreate = async (type, mode = 'new') => {
    navigate(`/builder/${type}/${mode}`);
  };

  const handleStartAIBuild = ({ description, jobUrl }) => {
    const type = aiBuildType || currentDocType.type;
    const jobContext = jobUrl
      ? `Job posting URL: ${jobUrl}\nUse this target job context while generating the ${typeLabel[type]?.toLowerCase() || 'document'}. If the URL content is not available, infer the likely role/company from the URL and let the user refine the job description.`
      : '';
    setAiBuildType(null);
    navigate(`/builder/${type}/ai`, {
      state: {
        aiSeed: {
          description,
          jobContext,
        },
      },
    });
  };

  const handleUseTemplate = async (templateId, type = currentDocType.type) => {
    navigate(`/builder/${type}/new?template=${templateId}`);
  };

  const handleUseExample = async (type = currentDocType.type) => {
    if (creatingExample) return;
    setCreatingExample(true);
    try {
      const data = createExampleDocumentData(type);
      const payload = {
        type,
        title: data?.personalInfo?.fullName || data?.jobInfo?.role || 'Example Document',
        template: data.template || 'modern',
        data,
      };
      const created = await api.createDoc(payload);
      setDocs(prev => [created, ...prev]);
      navigate(`/builder/${type}/${created._id}`);
    } catch (err) {
      showAlert('Could not create example: ' + err.message, { type: 'error', title: 'Example Failed' });
    } finally {
      setCreatingExample(false);
    }
  };

  const checkDownloadLocked = async (doc) => {
    let freshUser = user;
    try { freshUser = await refreshUser(); } catch {}
    const planActive = freshUser?.plan === 'pro' &&
      (!freshUser?.planExpiresAt || new Date(freshUser.planExpiresAt) > new Date());
    if (planActive) return [];

    try {
      const cfg = await api.getSectionConfig();
      const sections = cfg.premiumSections || [];
      setPremiumSections(sections);
      return getUsedPremiumSections(doc?.data || {}, sections);
    } catch {
      return getUsedPremiumSections(doc?.data || {}, premiumSections);
    }
  };

  const handleDownload = async (doc, format) => {
    const usedSections = await checkDownloadLocked(doc);
    if (usedSections.length) {
      setDownloadUpgrade({ doc, usedSections });
      return;
    }

    setDownloadingDoc(`${doc._id}-${format}`);
    try {
      const filename = getDownloadFilename(doc);
      await exportToPDF(`dashboard-preview-${doc._id}`, filename, { pageSize: doc?.data?.personalInfo?.pageSize });
    } catch (err) {
      showAlert('Download failed: ' + err.message, { type: 'error', title: 'Download Failed' });
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleDelete = async (id) => {
    const ok = await showConfirm('Are you sure you want to delete this document? This cannot be undone.', {
      title: 'Delete Document', confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    await api.deleteDoc(id);
    setDocs(prev => prev.filter(doc => doc._id !== id));
  };

  const fmtTimeAgo = (iso) => {
    const diff = Date.now() - new Date(iso);
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 172800000) return 'Yesterday';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderSidebar = () => (
    <aside className={`client-dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>

      {/* ── Top accent bar ── */}
      <div style={{ height: 3, background: 'linear-gradient(90deg,#EF1D28,#FF5C5C,#FB923C)', flexShrink: 0 }} />

      {/* ── Logo row ── */}
      <div style={{ padding: sidebarCollapsed ? '14px 10px' : '14px 16px', display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', minHeight: 58, borderBottom: '1px solid #F1F4F8' }}>
        {sidebarCollapsed ? (
          <button type="button" onClick={() => setSidebarCollapsed(false)} title="Expand sidebar"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, width: 36, height: 36, flexShrink: 0 }}>
            <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width: 30, height: 30, objectFit: 'contain' }} />
          </button>
        ) : (
          <>
            <button type="button" onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, minWidth: 0 }}>
              <img src="/assets/mars-logo.png" alt="MarsResume" style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#0F172A', letterSpacing: -0.4, lineHeight: 1.1 }}>MarsResume</div>
                <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Dashboard</div>
              </div>
            </button>
            {!sidebarOpen ? (
              <button type="button" onClick={() => setSidebarCollapsed(true)} title="Collapse"
                style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#94A3B8', cursor: 'pointer', padding: 5, display: 'flex', borderRadius: 8, transition: 'all 0.15s', flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F1F4F8'; e.currentTarget.style.color = '#475569'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = '#94A3B8'; }}>
                <Menu size={15} />
              </button>
            ) : (
              <button type="button" onClick={closeSidebar}
                style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#94A3B8', cursor: 'pointer', padding: 5, display: 'flex', borderRadius: 8, flexShrink: 0 }}>
                <X size={15} />
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Create New button ── */}
      <div style={{ padding: sidebarCollapsed ? '10px 10px 8px' : '10px 14px 8px' }}>
        {!sidebarCollapsed ? (
          <button type="button" onClick={() => handleCreate('resume')}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(135deg,#EF1D28 0%,#FF5C5C 100%)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 16px rgba(239,29,40,0.35)', letterSpacing: 0.1, transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(239,29,40,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(239,29,40,0.35)'; e.currentTarget.style.transform = 'none'; }}>
            <Plus size={16} strokeWidth={2.5} /> Create New
          </button>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button type="button" onClick={() => handleCreate('resume')} title="Create New"
              style={{ width: 42, height: 42, background: 'linear-gradient(135deg,#EF1D28,#FF5C5C)', color: 'white', border: 'none', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 14px rgba(239,29,40,0.35)', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(239,29,40,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(239,29,40,0.35)'; e.currentTarget.style.transform = 'none'; }}>
              <Plus size={18} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>

      {/* ── Nav ── */}
      <nav style={{ flex: 1, padding: sidebarCollapsed ? '6px 10px' : '6px 10px', overflowY: 'auto' }}>
        {!sidebarCollapsed && (
          <div style={{ fontSize: 10, fontWeight: 700, color: '#CBD5E1', letterSpacing: 1.4, padding: '6px 10px 10px', textTransform: 'uppercase' }}>Menu</div>
        )}

        {[
          { id: 'dashboard',    icon: LayoutDashboard,   label: 'Dashboard',     color: '#6366F1', bg: '#EEF2FF' },
          { id: 'interviews',   icon: BriefcaseBusiness,  label: 'Job Interview',  color: '#8B5CF6', bg: '#F5F3FF', proOnly: true },
          { id: 'job_search',   icon: Search,             label: 'Job Search',     color: '#0EA5E9', bg: '#E0F2FE' },
          { id: 'job_tracker',  icon: KanbanSquare,       label: 'Job Tracker',    color: '#14B8A6', bg: '#CCFBF1', badge: 'NEW', proOnly: true },
          { id: 'templates',    icon: ClipboardList,      label: 'Templates',      color: '#F59E0B', bg: '#FEF3C7' },
          { id: 'resume',       icon: FolderOpen,         label: 'My Documents',   color: '#10B981', bg: '#D1FAE5', matchFn: (v) => ['resume','cover_letter','cv'].includes(v) },
          { id: 'linkedin_audit', icon: Linkedin,         label: 'LinkedIn Audit', color: '#0A66C2', bg: '#E8F0FE', badge: 'NEW' },
          { id: 'subscription', icon: CreditCard,         label: 'My Plan',        color: '#EF4444', bg: '#FEE2E2' },
          { id: 'referral',     icon: Gift,               label: 'Refer & Earn',   color: '#EC4899', bg: '#FCE7F3', badge: 'NEW' },
        ].filter(item => !hiddenNavItems.includes(item.id)).map(item => {
          const isActive = item.matchFn ? item.matchFn(activeView) : activeView === item.id;
          const Icon = item.icon;
          const locked = item.proOnly && user?.plan !== 'pro';
          return (
            <button key={item.id} type="button" onClick={() => setView(item.id)} title={sidebarCollapsed ? (locked ? `${item.label} (Pro)` : item.label) : undefined}
              className={`dash-sidebar-nav-item${isActive ? ' active' : ''}`}
              style={{ justifyContent: sidebarCollapsed ? 'center' : 'flex-start', opacity: locked ? 0.75 : 1 }}>
              {/* Icon container */}
              <div style={{ position: 'relative', width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isActive ? 'linear-gradient(135deg,#EF1D28,#FF5C5C)' : item.bg, boxShadow: isActive ? '0 4px 14px rgba(239,29,40,0.30)' : 'none', transition: 'all 0.2s' }}>
                <Icon size={16} strokeWidth={isActive ? 2.3 : 1.9} style={{ color: isActive ? 'white' : item.color, transition: 'color 0.2s' }} />
                {locked && (
                  <div style={{ position: 'absolute', bottom: -3, right: -3, width: 14, height: 14, borderRadius: '50%', background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid white' }}>
                    <Lock size={7} color="white" strokeWidth={3} />
                  </div>
                )}
              </div>
              {!sidebarCollapsed && (
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
              )}
              {!sidebarCollapsed && locked && (
                <span style={{ fontSize: 9, fontWeight: 800, background: 'linear-gradient(135deg,#F59E0B,#FBBF24)', color: 'white', padding: '2px 6px', borderRadius: 20, letterSpacing: 0.5, flexShrink: 0 }}>
                  PRO
                </span>
              )}
              {!sidebarCollapsed && !locked && item.badge && !isActive && (
                <span style={{ fontSize: 9, fontWeight: 800, background: 'linear-gradient(135deg,#EC4899,#F472B6)', color: 'white', padding: '2px 6px', borderRadius: 20, letterSpacing: 0.5, flexShrink: 0 }}>
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}

      </nav>

      {/* ── User card (identity only — account actions live in the top-right profile menu) ── */}
      <div style={{ padding: sidebarCollapsed ? '8px 10px 12px' : '8px 12px 12px', flexShrink: 0 }}>
        {!sidebarCollapsed ? (
          <div style={{ background: 'linear-gradient(135deg,#F8FAFC,#F1F4F8)', border: '1px solid #E2E8F0', borderRadius: 14, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
            {avatar
              ? <img src={avatar} alt="" style={{ width: 38, height: 38, borderRadius: 11, objectFit: 'cover', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
              : <div style={{ width: 38, height: 38, borderRadius: 11, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0, boxShadow: '0 3px 10px rgba(239,29,40,0.30)' }}>
                  {(displayName || 'U').charAt(0).toUpperCase()}
                </div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3, background: user.plan === 'pro' ? 'linear-gradient(135deg,#FEF3C7,#FDE68A)' : '#F1F5F9', borderRadius: 20, padding: '1px 8px 1px 5px', border: user.plan === 'pro' ? '1px solid #F59E0B' : '1px solid #E2E8F0' }}>
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: user.plan === 'pro' ? '#F59E0B' : '#22C55E', flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: user.plan === 'pro' ? '#92400E' : '#475569', textTransform: 'capitalize', letterSpacing: 0.2 }}>{user.plan || 'Free'} Plan</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} title={displayName}>
            {avatar
              ? <img src={avatar} alt="" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
              : <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, boxShadow: '0 3px 10px rgba(239,29,40,0.30)' }}>
                  {(displayName || 'U').charAt(0).toUpperCase()}
                </div>}
          </div>
        )}
      </div>
    </aside>
  );

  const renderDashboardHome = () => {
    const features = [
      { icon: Sparkles,         label: 'AI Resume Chat',  desc: 'Improve bullets with contextual AI.',         onClick: () => navigate('/builder/resume/ai'), color: '#EF1D28', bg: '#FFF1F2', gradient: 'linear-gradient(135deg,#EF1D28,#FB923C)', tag: 'Popular' },
      { icon: ClipboardList,    label: 'ATS Templates',   desc: 'Browse 9+ ATS-optimised templates.',          onClick: () => setView('templates'),             color: '#1D4ED8', bg: '#EFF6FF', gradient: 'linear-gradient(135deg,#1D4ED8,#6366F1)', tag: null },
      { icon: BriefcaseBusiness,label: 'Job Interviews',  desc: 'Practice questions for your target role.',    onClick: () => setView('interviews'),            color: '#7C3AED', bg: '#F5F3FF', gradient: 'linear-gradient(135deg,#7C3AED,#A78BFA)', tag: null, proOnly: true },
      { icon: Mail,             label: 'Cover Letters',   desc: 'Generate personalised cover letters fast.',   onClick: () => handleCreate('cover_letter'),      color: '#059669', bg: '#ECFDF5', gradient: 'linear-gradient(135deg,#059669,#10B981)', tag: null },
    ];

    const docTypes = [
      { type: 'resume',       icon: '📄', label: 'Resume',      desc: 'Professional resume for job applications', color: '#1D4ED8', bg: '#EFF6FF' },
      { type: 'cover_letter', icon: '✉️', label: 'Cover Letter', desc: 'Personalised cover letter for any role',   color: '#059669', bg: '#ECFDF5' },
      { type: 'cv',           icon: '🎓', label: 'Academic CV',  desc: 'Detailed CV for academic & research roles',color: '#7C3AED', bg: '#F5F3FF' },
    ];

    const firstName = user.name?.split(' ')[0] || 'there';
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    const greetingEmoji = hour < 12 ? '☀️' : hour < 18 ? '👋' : '🌙';
    const totalDocs = docs.length;
    const isPro = user.plan === 'pro';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Hero Banner ── */}
        <div style={{ background: 'linear-gradient(150deg,#060D1A 0%,#0D1120 50%,#111827 100%)', borderRadius: 22, padding: '28px 32px', position: 'relative', overflow: 'hidden', border: '1px solid #1A2A3E' }}>
          {/* decorative blobs */}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 82% 50%,rgba(239,29,40,0.16) 0%,transparent 50%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: -40, right: 100, width: 220, height: 220, borderRadius: '50%', background: 'rgba(239,29,40,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -30, right: -20, width: 140, height: 140, borderRadius: '50%', background: 'rgba(29,78,216,0.07)', pointerEvents: 'none' }} />
          {/* top border glow */}
          <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(239,29,40,0.35),transparent)', pointerEvents: 'none' }} />
          {/* dot grid */}
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.035) 1px,transparent 1px)', backgroundSize: '24px 24px', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 500, marginBottom: 10, letterSpacing: '0.03em' }}>
                {greetingEmoji} {greeting}
              </div>
              <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 900, color: 'white', letterSpacing: -0.8, lineHeight: 1.1 }}>
                Welcome back, <span style={{ background: 'linear-gradient(90deg,#FF5C5C,#FB923C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{firstName}</span>
              </h1>
              <p style={{ margin: '0 0 18px', fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, maxWidth: 440 }}>
                Your career dashboard — everything you need to land your next role.
              </p>
              {/* quick stats row */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {[
                  { val: totalDocs, label: 'Documents' },
                  { val: docCount['resume'] || 0, label: 'Resumes' },
                  { val: docCount['cover_letter'] || 0, label: 'Cover Letters' },
                ].map(({ val, label }) => (
                  <div key={label} style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 22, fontWeight: 900, color: 'white', lineHeight: 1, letterSpacing: -0.5 }}>{val}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2, fontWeight: 500, letterSpacing: '0.03em' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
              <button type="button" onClick={() => handleCreate('resume')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#EF1D28,#FF5C5C)', color: 'white', border: 'none', borderRadius: 12, padding: '13px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', boxShadow: '0 4px 20px rgba(239,29,40,0.45)', whiteSpace: 'nowrap', letterSpacing: 0.1 }}>
                <Sparkles size={15} /> Create with AI
              </button>
              {!isPro && (
                <button type="button" onClick={() => setView('subscription')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.25)', color: '#FB923C', borderRadius: 10, padding: '8px 16px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', whiteSpace: 'nowrap' }}>
                  <Crown size={12} /> Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="dash-home-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>

          {/* Documents card */}
          <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 16, padding: '20px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FolderOpen size={17} color="#1D4ED8" />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>My Documents</span>
              </div>
              <button type="button" onClick={() => setView('resume')} style={{ fontSize: 11, color: '#EF1D28', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', gap: 3 }}>
                View all <ChevronRight size={11} strokeWidth={2.5} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 0 }}>
              {DASHBOARD_DOC_TABS.map((tab, i) => (
                <div key={tab.type} style={{ flex: 1, paddingRight: 14, borderRight: i < DASHBOARD_DOC_TABS.length - 1 ? '1px solid #F0F2F5' : 'none', paddingLeft: i > 0 ? 14 : 0 }}>
                  <div style={{ fontSize: 32, fontWeight: 900, color: '#111827', lineHeight: 1, letterSpacing: -1.5 }}>{docCount[tab.type] || 0}</div>
                  <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 5, fontWeight: 500 }}>{tab.singular}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Plan card */}
          <div style={{ background: isPro ? 'linear-gradient(135deg,#0F172A,#1A2332)' : 'linear-gradient(135deg,#18181B,#27272A)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: '20px 22px', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 20px rgba(15,23,42,0.18)' }}>
            <div style={{ position: 'absolute', top: -25, right: -25, width: 110, height: 110, borderRadius: '50%', background: isPro ? 'rgba(251,146,60,0.12)' : 'rgba(239,29,40,0.08)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -15, left: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(99,102,241,0.07)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: isPro ? 'rgba(251,146,60,0.2)' : 'rgba(239,29,40,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Crown size={17} color={isPro ? '#FB923C' : '#FF5C5C'} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current Plan</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: 'white', letterSpacing: -0.8, marginBottom: 6, textTransform: 'capitalize', lineHeight: 1 }}>{user.plan || 'Free'}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', lineHeight: 1.5 }}>
                {isPro ? 'Unlimited documents & all AI tools' : 'Upgrade for unlimited documents & priority AI'}
              </div>
              {!isPro && (
                <button type="button" onClick={() => setView('subscription')}
                  style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 13px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  <Sparkles size={11} /> Go Pro
                </button>
              )}
            </div>
          </div>

          {/* Job Search card */}
          <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 16, padding: '20px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', bottom: -20, right: -20, width: 90, height: 90, borderRadius: '50%', background: '#FFF1F2', pointerEvents: 'none' }} />
            {!isPro && (
              <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 3, background: '#F1F5F9', borderRadius: 20, padding: '2px 8px' }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: '#64748B', letterSpacing: '0.04em' }}>1/MO FREE</span>
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                  <Search size={17} color="#EF1D28" />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Job Search</span>
              </div>
              <p style={{ margin: '0 0 16px', fontSize: 12, color: '#9CA3AF', lineHeight: 1.65 }}>
                {isPro ? 'Find roles on LinkedIn, Indeed & Glassdoor — tailored to your profile with AI.' : 'Search jobs on LinkedIn, Indeed & Glassdoor — 1 free search a month, unlimited with Pro.'}
              </p>
              <button type="button" onClick={() => setView('job_search')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#EF1D28,#FF5C5C)', color: 'white', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', boxShadow: '0 3px 10px rgba(239,29,40,0.3)' }}>
                Browse Jobs <ChevronRight size={13} strokeWidth={2.5} />
              </button>
            </div>
          </div>

        </div>

        {/* ── AI Tools ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#111827', letterSpacing: -0.2 }}>AI Tools</h2>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>Powered by GPT-4</span>
            </div>
          </div>
          <div className="dash-home-features" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {features.map(f => {
              const Icon = f.icon;
              const cardLocked = f.proOnly && !isPro;
              return (
                <button key={f.label} type="button" onClick={f.onClick}
                  style={{ background: 'white', border: `1px solid ${cardLocked ? '#FDE68A' : '#F0F2F5'}`, borderRadius: 16, padding: '20px 18px', cursor: 'pointer', textAlign: 'left', fontFamily: 'Inter,sans-serif', transition: 'all 0.18s', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = cardLocked ? '0 10px 28px rgba(245,158,11,0.15)' : '0 10px 28px rgba(0,0,0,0.10)'; e.currentTarget.style.borderColor = cardLocked ? '#FCD34D' : '#E5E7EB'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = cardLocked ? '#FDE68A' : '#F0F2F5'; }}>
                  {cardLocked ? (
                    <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center', gap: 3, background: 'linear-gradient(135deg,#F59E0B,#FBBF24)', borderRadius: 20, padding: '2px 7px' }}>
                      <Lock size={8} color="white" strokeWidth={3} />
                      <span style={{ fontSize: 9, fontWeight: 800, color: 'white', letterSpacing: '0.04em' }}>PRO</span>
                    </div>
                  ) : f.tag ? (
                    <div style={{ position: 'absolute', top: 12, right: 12, background: f.bg, color: f.color, fontSize: 9, fontWeight: 800, borderRadius: 20, padding: '2px 7px', letterSpacing: '0.04em', border: `1px solid ${f.color}22` }}>
                      {f.tag}
                    </div>
                  ) : null}
                  <div style={{ width: 44, height: 44, borderRadius: 13, background: cardLocked ? 'linear-gradient(135deg,#D1D5DB,#9CA3AF)' : f.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.18)', position: 'relative' }}>
                    <Icon size={20} color="white" strokeWidth={1.8} />
                    {cardLocked && (
                      <div style={{ position: 'absolute', bottom: -3, right: -3, width: 16, height: 16, borderRadius: '50%', background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                        <Lock size={7} color="white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: cardLocked ? '#6B7280' : '#111827', marginBottom: 6, letterSpacing: -0.1 }}>{f.label}</div>
                  <div style={{ fontSize: 11.5, color: '#9CA3AF', lineHeight: 1.6 }}>{cardLocked ? 'Available on Pro plan. Upgrade to unlock.' : f.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Bottom: Job Openings | Interviews | Profile Strength | Create New ── */}
        <div className="dash-home-bottom" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 290px 290px', gap: 14, alignItems: 'start' }}>

          {/* Job Openings */}
          <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BriefcaseBusiness size={15} color="#EF1D28" />
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>Job Openings</span>
              </div>
              <button type="button" onClick={() => setView('job_search')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: isPro ? '#F8FAFC' : 'linear-gradient(135deg,#F59E0B,#FBBF24)', border: isPro ? '1px solid #F0F2F5' : 'none', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: isPro ? 600 : 700, color: isPro ? '#6B7280' : 'white', cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                {isPro ? <><span>See all</span> <ChevronRight size={11} strokeWidth={2.5} /></> : <><Lock size={9} strokeWidth={2.5} /><span>Pro</span></>}
              </button>
            </div>
            {dashJobsLoading ? (
              <div style={{ padding: '32px 18px', textAlign: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                  <BriefcaseBusiness size={17} color="#D1D5DB" />
                </div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>Finding jobs…</div>
              </div>
            ) : dashJobs.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 13, background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <BriefcaseBusiness size={22} color="#FECACA" />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>No openings right now</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 14, lineHeight: 1.6 }}>
                  {docs.find(d => d.type === 'resume')?.data?.personalInfo?.jobTitle
                    ? 'Check back soon or search manually.'
                    : 'Add a job title to your resume to see matched openings.'}
                </div>
                <button type="button" onClick={() => setView('job_search')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#FFF1F2', color: '#EF1D28', border: '1px solid #FECACA', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  Browse Jobs <ChevronRight size={11} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              dashJobs.map((job, i) => {
                const company = job.company_name || job.company || 'Company';
                const initial = company.charAt(0).toUpperCase();
                const colors = ['#EF1D28','#1D4ED8','#059669','#7C3AED','#D97706','#0891B2'];
                const bg = colors[i % colors.length];
                return (
                  <a key={job.id || i} href={job.url} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: i < dashJobs.length - 1 ? '1px solid #F9FAFB' : 'none', textDecoration: 'none', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: job.logo ? 'white' : bg, border: job.logo ? '1px solid #F0F2F5' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14, fontWeight: 900, color: 'white', overflow: 'hidden' }}>
                      {job.logo ? <img src={job.logo} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} /> : initial}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</div>
                      <div style={{ fontSize: 11, color: '#EF1D28', marginTop: 2, fontWeight: 600 }}>{company}</div>
                    </div>
                    <ChevronRight size={13} color="#D1D5DB" strokeWidth={2} style={{ flexShrink: 0 }} />
                  </a>
                );
              })
            )}
          </div>

          {/* Interviews */}
          <div style={{ background: 'white', border: `1px solid ${isPro ? '#F0F2F5' : '#FDE68A'}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: isPro ? '#F5F3FF' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                  <BriefcaseBusiness size={15} color={isPro ? '#7C3AED' : '#D97706'} />
                  {!isPro && (
                    <div style={{ position: 'absolute', bottom: -2, right: -2, width: 13, height: 13, borderRadius: '50%', background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid white' }}>
                      <Lock size={6} color="white" strokeWidth={3} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>Interview Prep</span>
                  {!isPro && (
                    <span style={{ fontSize: 9, fontWeight: 800, background: 'linear-gradient(135deg,#F59E0B,#FBBF24)', color: 'white', padding: '2px 6px', borderRadius: 20, letterSpacing: 0.5 }}>PRO</span>
                  )}
                </div>
              </div>
              {isPro ? (
                <button type="button" onClick={() => setView('interviews')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#F8FAFC', border: '1px solid #F0F2F5', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#6B7280', cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  See all <ChevronRight size={11} strokeWidth={2.5} />
                </button>
              ) : (
                <button type="button" onClick={() => setView('subscription')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'linear-gradient(135deg,#F59E0B,#FBBF24)', border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, color: 'white', cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  <Lock size={9} strokeWidth={2.5} /> Upgrade
                </button>
              )}
            </div>
            {!isPro ? (
              <div style={{ padding: '28px 20px', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', border: '1px solid #FCD34D', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <Lock size={22} color="#F59E0B" strokeWidth={1.8} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Interview Prep is Pro-only</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 16, lineHeight: 1.6 }}>Practice role-specific interview questions with AI-powered model answers. Upgrade to unlock.</div>
                <button type="button" onClick={() => setView('subscription')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#F59E0B,#FBBF24)', color: 'white', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', boxShadow: '0 3px 10px rgba(245,158,11,0.3)' }}>
                  <Sparkles size={11} /> Upgrade to Pro
                </button>
              </div>
            ) : dashInterviews.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 13, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <BriefcaseBusiness size={22} color="#C4B5FD" />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>No practice sessions yet</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 14, lineHeight: 1.6 }}>Generate role-specific questions and AI model answers to nail your next interview.</div>
                <button type="button" onClick={() => setView('interviews')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#F5F3FF', color: '#7C3AED', border: '1px solid #EDE9FE', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  Start Practice <ChevronRight size={11} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              dashInterviews.slice(0, 6).map((iv, i) => {
                const emoji = getJobEmoji(iv.title || '');
                return (
                  <div key={iv._id || i} onClick={() => setView('interviews')}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: i < Math.min(dashInterviews.length, 6) - 1 ? '1px solid #F9FAFB' : 'none', cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: '#F5F3FF', border: '1px solid #EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                      {emoji}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{iv.title || 'Untitled'}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{iv.questions?.length || 0} questions · {iv.seniority || 'mid'}</div>
                    </div>
                    <ChevronRight size={13} color="#D1D5DB" strokeWidth={2} style={{ flexShrink: 0 }} />
                  </div>
                );
              })
            )}
          </div>

          {/* Profile Strength */}
          <ProfileStrengthCard profile={profile} docs={docs} onSectionClick={goToProfileSection} />

          {/* Create New */}
          <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#EF1D28,#FF5C5C)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,29,40,0.3)' }}>
                <Plus size={16} color="white" strokeWidth={2.5} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>Create New</span>
            </div>
            <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {docTypes.map(dt => (
                <button key={dt.type} type="button" onClick={() => handleCreate(dt.type)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#FAFAFA', border: '1px solid #F0F2F5', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', fontFamily: 'Inter,sans-serif', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = dt.bg; e.currentTarget.style.borderColor = dt.color + '33'; e.currentTarget.style.transform = 'translateX(3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#F0F2F5'; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: dt.bg, border: `1px solid ${dt.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                    {dt.icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: '#111827' }}>{dt.label}</div>
                    <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 1, lineHeight: 1.4 }}>{dt.desc}</div>
                  </div>
                  <ChevronRight size={14} color="#D1D5DB" strokeWidth={2} style={{ flexShrink: 0, marginLeft: 'auto' }} />
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    );
  };

  const renderDocuments = () => {
    const EmptyIcon = isOverviewView ? FolderOpen : currentDocType.icon;
    const emptyLabel = isOverviewView ? 'documents' : currentDocType.label.toLowerCase();

    return (
      <>
        <input ref={uploadInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleUploadResume} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 22, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 3px', color: '#111827', fontSize: 24, fontWeight: 700, letterSpacing: -0.4 }}>My Documents</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#9CA3AF' }}>
              {docs.length} document{docs.length !== 1 ? 's' : ''} · Unlimited
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => uploadInputRef.current?.click()} style={{ border: '1.5px solid #E5E7EB', background: 'white', color: '#374151', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}>
              <Upload size={15} strokeWidth={2.5} /> Upload Resume
            </button>
            <button type="button" onClick={() => handleCreate(currentDocType.type)} style={{ border: 'none', background: 'linear-gradient(135deg, #EF1D28, #C5161D)', color: 'white', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 14px rgba(239,29,40,0.30)' }}>
              <Plus size={15} strokeWidth={2.5} /> Create New
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid #EAECF0', marginBottom: 24, overflowX: 'auto' }}>
          {DASHBOARD_DOC_TABS.map(tab => {
            const active = activeView === tab.id;
            const count = docCount[tab.type] || 0;
            return (
              <button key={tab.id} type="button" onClick={() => setView(tab.id)}
                style={{ border: 'none', background: 'transparent', color: active ? '#EF1D28' : '#6B7280', borderBottom: active ? '2px solid #EF1D28' : '2px solid transparent', padding: '0 20px 13px 0', fontSize: 14, fontWeight: active ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {tab.label}
                {count > 0 && (
                  <span style={{ background: active ? '#EF1D2820' : '#F3F4F6', color: active ? '#EF1D28' : '#9CA3AF', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 600, lineHeight: '18px' }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          <button type="button" onClick={() => setView('templates')} style={{ border: 'none', background: 'transparent', color: '#6B7280', borderBottom: '2px solid transparent', padding: '0 20px 13px 0', fontSize: 14, fontWeight: 400, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}>
            Templates
          </button>
        </div>

        <div className="client-dashboard-card-grid">
          <CreateActionsPanel
            docType={currentDocType}
            onCreate={() => handleCreate(currentDocType.type)}
            onCreateAI={() => setAiBuildType(currentDocType.type)}
            onUseExample={() => handleUseExample(currentDocType.type)}
            onUpload={() => uploadInputRef.current?.click()}
            creatingExample={creatingExample}
          />

          {loading ? (
            <div style={{ minHeight: 464, background: '#FFFFFF', borderRadius: 17, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RocketLoader size={160} message="Loading documents…" />
            </div>
          ) : filteredDocs.length > 0 ? (
            filteredDocs.map(doc => (
              <DashboardDocumentCard
                key={doc._id}
                doc={doc}
                onEdit={() => navigate(`/builder/${doc.type}/${doc._id}`)}
                onDelete={() => handleDelete(doc._id)}
                onDownloadPDF={() => handleDownload(doc, 'pdf')}
                downloading={downloadingDoc?.startsWith(`${doc._id}-`)}
              />
            ))
          ) : (
            <div style={{ minHeight: 464, background: '#FFFFFF', borderRadius: 17, border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 26, color: '#64748B' }}>
              <EmptyIcon size={42} color={currentDocType.color} />
              <div style={{ marginTop: 16, fontSize: 18, fontWeight: 800, color: '#2E2E2E' }}>No {emptyLabel} yet</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>Create one from scratch or use AI to get started.</div>
            </div>
          )}

          {/* PremiumPanel hidden — all features free */}
        </div>

      </>
    );
  };

  const TEMPLATE_DOC_TABS = [
    { type: 'resume',       label: 'Resume' },
    { type: 'cover_letter', label: 'Cover Letter' },
    { type: 'cv',           label: 'Academic CV' },
  ];

  const renderTemplates = () => {
    const activeList = templateDocType === 'cover_letter' ? CL_TEMPLATES : templateDocType === 'cv' ? CV_TEMPLATES : TEMPLATES;
    const sortedTemplates = [...activeList].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    const totalCount = activeList.length;

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
          <div>
            <h1 style={{ margin: '0 0 4px', color: '#111827', fontSize: 24, fontWeight: 700, letterSpacing: -0.4 }}>Templates</h1>
            <p style={{ margin: 0, color: '#9CA3AF', fontSize: 13 }}>
              {totalCount} templates · Pick a layout and start building
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ background: '#DCFCE7', color: '#15803D', borderRadius: 999, padding: '6px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #BBF7D0', whiteSpace: 'nowrap' }}>
              All templates free
            </span>
          </div>
        </div>

        {/* Doc-type tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, background: '#F3F4F6', borderRadius: 12, padding: 4, width: 'fit-content' }}>
          {TEMPLATE_DOC_TABS.map(tab => {
            const active = templateDocType === tab.type;
            return (
              <button key={tab.type} type="button" onClick={() => setTemplateDocType(tab.type)}
                style={{ background: active ? 'white' : 'transparent', color: active ? '#111827' : '#6B7280', border: 'none', borderRadius: 9, padding: '7px 18px', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', boxShadow: active ? '0 1px 4px rgba(0,0,0,0.10)' : 'none', whiteSpace: 'nowrap' }}>
                {tab.label}
              </button>
            );
          })}
        </div>

        {templateDocType === 'cover_letter' ? (
          <div className="client-dashboard-template-grid">
            {CL_TEMPLATES.map(tmpl => (
              <CoverLetterTemplateCard key={tmpl.id} tmpl={tmpl} onUse={handleUseTemplate} />
            ))}
          </div>
        ) : (
          <div className="client-dashboard-template-grid">
            {sortedTemplates.map(tmpl => (
              <TemplateCard key={tmpl.id} tmpl={tmpl} onUse={handleUseTemplate} activeDocType={templateDocType} />
            ))}
          </div>
        )}

      </div>
    );
  };

  const FREE_FEATURES = [
    'Up to 3 documents',
    'All 9 professional templates',
    'AI resume generation (limited)',
    'PDF export',
    'Live preview editor',
    'All section types',
  ];
  const PRO_FEATURES = [
    'Unlimited documents',
    'All 9 professional templates',
    'Unlimited AI generation',
    'AI resume tailoring (ATS)',
    'AI bullet point generator',
    'PDF export',
    'Live preview editor',
    'Google login',
    'Priority support',
    'Early access to new features',
  ];

  const renderSubscription = () => {
    const isPro = user.plan === 'pro';
    const usedDocs = docs.length;
    const pct = Math.min((usedDocs / 3) * 100, 100);
    const COMPARE_ROWS = [
      { label: 'Documents',       free: '3 max',    pro: 'Unlimited' },
      { label: 'Templates',       free: 'All',      pro: 'All + future' },
      { label: 'AI Generation',   free: 'Limited',  pro: 'Unlimited' },
      { label: 'AI Tailoring',    free: false,      pro: true },
      { label: 'Bullet Generator',free: false,      pro: true },
      { label: 'PDF Export',      free: true,       pro: true },
      { label: 'Interview AI',    free: true,       pro: true },
      { label: 'Priority Support',free: false,      pro: true },
    ];

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 }}>
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700, color: '#111827', letterSpacing: -0.4 }}>My Plan</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#9CA3AF' }}>Manage your subscription and billing details.</p>
          </div>
          <span style={{
            background: isPro ? 'linear-gradient(135deg,#FB923C,#F97316)' : '#F3F4F6',
            color: isPro ? 'white' : '#6B7280',
            borderRadius: 999, padding: '6px 16px', fontSize: 12, fontWeight: 700,
            boxShadow: isPro ? '0 2px 8px rgba(249,115,22,0.30)' : 'none',
          }}>
            {isPro ? '⚡ Pro Active' : 'Free Plan'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Plan banner */}
            <div style={{
              borderRadius: 18, padding: '26px 28px', position: 'relative', overflow: 'hidden',
              background: isPro ? 'linear-gradient(135deg,#111827 0%,#1E293B 100%)' : 'linear-gradient(135deg,#1E293B 0%,#0F172A 100%)',
            }}>
              {/* decorative blob */}
              <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: isPro ? 'rgba(251,146,60,0.15)' : 'rgba(239,29,40,0.12)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: -30, left: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

              <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 13, background: isPro ? 'linear-gradient(135deg,#FB923C,#F97316)' : 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: isPro ? '0 4px 16px rgba(249,115,22,0.40)' : '0 4px 16px rgba(239,29,40,0.40)' }}>
                      {isPro ? <Crown size={22} color="white" /> : <Zap size={22} color="white" />}
                    </div>
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'white', letterSpacing: -0.3 }}>{isPro ? 'Pro Plan' : 'Free Plan'}</div>
                      <div style={{ fontSize: 12, color: isPro ? '#FB923C' : '#9CA3AF', fontWeight: 500, marginTop: 1 }}>
                        {isPro ? 'All features unlocked' : 'Limited access'}
                      </div>
                    </div>
                  </div>

                  {!isPro && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>Documents used</span>
                        <span style={{ fontSize: 13, color: 'white', fontWeight: 700 }}>{usedDocs} / 3</span>
                      </div>
                      <div style={{ height: 7, background: 'rgba(255,255,255,0.12)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#EF1D28' : 'linear-gradient(90deg,#EF1D28,#FB923C)', borderRadius: 4, transition: 'width 0.5s' }} />
                      </div>
                      <div style={{ fontSize: 11, color: pct >= 100 ? '#FCA5A5' : 'rgba(255,255,255,0.45)', marginTop: 6 }}>
                        {usedDocs >= 3 ? 'Limit reached — upgrade to create more' : `${3 - usedDocs} slot${3 - usedDocs !== 1 ? 's' : ''} remaining`}
                      </div>
                    </div>
                  )}

                  {isPro && (
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                      Unlimited documents · Unlimited AI · All features
                    </div>
                  )}
                </div>

                {isPro && (
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 30, fontWeight: 800, color: 'white', lineHeight: 1 }}>$19.99</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>/month</div>
                  </div>
                )}
              </div>
            </div>

            {/* Included features */}
            <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 16, padding: '20px 22px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>
                {isPro ? "What's included in Pro" : 'Your Free plan includes'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px 20px' }}>
                {(isPro ? PRO_FEATURES : FREE_FEATURES).map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ width: 18, height: 18, borderRadius: 5, background: isPro ? '#ECFDF5' : '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckCircle2 size={11} color={isPro ? '#10B981' : '#EF1D28'} strokeWidth={2.5} />
                    </div>
                    <span style={{ fontSize: 13, color: '#374151' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Upgrade CTA (Free) or Billing (Pro) */}
            {isPro ? (
              <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 16, padding: '20px 22px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Billing & Subscription</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { icon: <CreditCard size={15} color="#6B7280" />, title: 'Payment Method', sub: 'Managed securely by our billing provider', badge: 'On file', badgeStyle: { color: '#6B7280', background: '#F3F4F6' } },
                    { icon: <ShieldCheck size={15} color="#10B981" />, title: 'Subscription Status', sub: 'Auto-renews monthly', badge: 'Active', badgeStyle: { color: '#10B981', background: '#ECFDF5', fontWeight: 700 } },
                  ].map(row => (
                    <div key={row.title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#F9FAFB', borderRadius: 10, border: '1px solid #F1F3F5' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {row.icon}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{row.title}</div>
                          <div style={{ fontSize: 11, color: '#9CA3AF' }}>{row.sub}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, ...row.badgeStyle }}>{row.badge}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, padding: '14px 16px', background: '#F9FAFB', border: '1px solid #F1F3F5', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 3 }}>Manage billing</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>Update your payment method, view invoices, or cancel your subscription.</div>
                  <button type="button"
                    onClick={handleManageBilling}
                    style={{ border: '1.5px solid #E5E7EB', background: 'white', color: '#111827', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Manage Billing →
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ background: 'linear-gradient(135deg,#0F172A,#1E293B)', borderRadius: 16, padding: '22px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(239,29,40,0.15)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'white', marginBottom: 4 }}>Unlock everything with Pro</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 16 }}>From $19.99/month · $160/year</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 20 }}>
                    {['Unlimited documents (no 3-doc cap)', 'Unlimited AI generation & tailoring', 'AI bullet point generator', 'ATS resume optimization', 'Priority support'].map(f => (
                      <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{ width: 18, height: 18, borderRadius: 5, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <CheckCircle2 size={11} color="white" strokeWidth={2.5} />
                        </div>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.80)' }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => navigate('/pricing')}
                    style={{ width: '100%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', border: 'none', borderRadius: 10, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(239,29,40,0.40)', fontFamily: 'Inter,sans-serif' }}>
                    Upgrade to Plus — from $19.99/mo →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: comparison table */}
          <div style={{ background: 'white', border: '1px solid #EAECF0', borderRadius: 16, overflow: 'hidden', position: 'sticky', top: 0 }}>
            <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Plan Comparison</div>
            </div>
            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 74px 74px', padding: '10px 20px', borderBottom: '1px solid #F1F3F5', background: '#F9FAFB' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Feature</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: isPro ? '#9CA3AF' : '#111827', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Free</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#F97316', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pro</div>
            </div>
            {COMPARE_ROWS.map((row, i) => (
              <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr 74px 74px', padding: '10px 20px', borderBottom: i < COMPARE_ROWS.length - 1 ? '1px solid #F9FAFB' : 'none', background: i % 2 === 0 ? 'white' : '#FAFBFC' }}>
                <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{row.label}</span>
                <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {typeof row.free === 'boolean'
                    ? row.free ? <CheckCircle2 size={14} color="#10B981" strokeWidth={2.5} /> : <span style={{ color: '#E2E8F0', fontSize: 18, lineHeight: 1 }}>—</span>
                    : <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280' }}>{row.free}</span>}
                </div>
                <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {typeof row.pro === 'boolean'
                    ? row.pro ? <CheckCircle2 size={14} color="#F97316" strokeWidth={2.5} /> : <span style={{ color: '#E2E8F0', fontSize: 18, lineHeight: 1 }}>—</span>
                    : <span style={{ fontSize: 11, fontWeight: 700, color: '#F97316' }}>{row.pro}</span>}
                </div>
              </div>
            ))}
            {!isPro && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid #F1F3F5' }}>
                <button type="button" onClick={() => navigate('/pricing')}
                  style={{ width: '100%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', border: 'none', borderRadius: 9, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 3px 10px rgba(239,29,40,0.30)', fontFamily: 'Inter,sans-serif' }}>
                  Upgrade Now →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`client-dashboard-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <DashboardResponsiveStyles />
      <button type="button" className="client-dashboard-mobile-menu" onClick={() => { setSidebarCollapsed(false); setSidebarOpen(true); }} style={{ ...dashboardIconButton, display: sidebarOpen ? 'none' : undefined, boxShadow: '0 8px 20px rgba(15,23,42,0.14)' }}>
        <Menu size={20} />
      </button>
      {sidebarOpen && <button type="button" className="client-dashboard-overlay" aria-label="Close menu" onClick={closeSidebar} />}
      {renderSidebar()}

      <main className="client-dashboard-main">
        <div className="client-dashboard-topbar">
          <div style={{ position: 'relative' }} onMouseLeave={() => setProfileMenuOpen(false)}>
            <button type="button" onClick={() => setProfileMenuOpen(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: profileMenuOpen ? '#F1F5F9' : 'transparent', border: `1px solid ${profileMenuOpen ? '#CBD5E1' : 'transparent'}`, borderRadius: 30, padding: '5px 10px 5px 5px', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'Inter, sans-serif' }}>
              {avatar
                ? <img src={avatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                    {(displayName || 'U').charAt(0).toUpperCase()}
                  </div>}
              <span className="dash-topbar-name" style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
              <ChevronDown size={14} color="#94A3B8" style={{ transform: profileMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
            </button>
            {profileMenuOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,23,42,0.14)', zIndex: 200, overflow: 'hidden', minWidth: 200 }}>
                {[
                  { label: 'Profile',          icon: UserRound,     view: 'profile', subTab: 'profile' },
                  { label: 'Account Settings', icon: Settings,      view: 'profile', subTab: 'account' },
                  { label: 'Feedback',         icon: MessageSquare, view: 'feedback' },
                  { label: 'Contact Us',       icon: HeadphonesIcon, view: 'contact' },
                ].map(item => (
                  <button key={item.label} type="button"
                    onClick={() => { if (item.subTab) { setProfileSubTab(item.subTab); setProfileNavNonce(n => n + 1); } setView(item.view); setProfileMenuOpen(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151', fontFamily: 'Inter, sans-serif', textAlign: 'left', transition: 'background 0.1s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                    <item.icon size={15} color="#6B7280" />
                    {item.label}
                  </button>
                ))}
                <div style={{ height: 1, background: '#F1F5F9', margin: '4px 0' }} />
                <button type="button" onClick={() => { setProfileMenuOpen(false); logout(); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#EF1D28', fontFamily: 'Inter, sans-serif', textAlign: 'left', transition: 'background 0.1s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                  <LogOut size={15} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="client-dashboard-workspace">
          <AnnouncementList announcements={announcements} />
          {activeView === 'profile' ? (
            <ProfileTab user={user} onSave={updateProfile} onDeleted={() => { logout(); navigate('/signup'); }} initialTab={profileSubTab} navNonce={profileNavNonce} docs={docs} initialSection={profileSectionLink} onNavigateResume={() => setView('resume')} />
          ) : activeView === 'subscription' ? (
            renderSubscription()
          ) : activeView === 'templates' ? (
            renderTemplates()
          ) : activeView === 'interviews' ? (
            <InterviewsPage docs={docs} initialInterview={pendingInterview} onInitialLoaded={() => setPendingInterview(null)} />
          ) : activeView === 'job_search' ? (
            <JobSearchPage onNavigate={(target, data) => {
              if (target === 'interviews') {
                if (data?.interview) setPendingInterview(data.interview);
                setView('interviews');
              } else if (target === 'job_tracker') setView('job_tracker');
              else if (target === 'resume') navigate('/builder/resume/new');
              else if (target === 'cover_letter') navigate('/builder/cover_letter/new');
            }} />
          ) : activeView === 'job_tracker' ? (
            <JobTrackerPage onFindJobs={() => setView('job_search')} />
          ) : activeView === 'linkedin_audit' ? (
            <LinkedInAuditPage user={user} onOpenJobSearch={() => setView('job_search')} />
          ) : activeView === 'referral' ? (
            <ReferralTab user={user} />
          ) : activeView === 'feedback' ? (
            <FeedbackTab user={user} />
          ) : activeView === 'contact' ? (
            <ContactTab user={user} />
          ) : activeView === 'dashboard' ? (
            renderDashboardHome()
          ) : (
            renderDocuments()
          )}
        </div>
      </main>

      {downloadUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setDownloadUpgrade(null)}>
          <div style={{ background: 'white', borderRadius: 20, padding: '36px 32px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', position: 'relative' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setDownloadUpgrade(null)} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF', lineHeight: 1 }}>x</button>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#F97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Lock size={28} color="white" />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Switch to Pro to Download</div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 8 }}>
              This {typeLabel[downloadUpgrade.doc?.type]?.toLowerCase() || 'document'} includes <strong style={{ color: '#111827' }}>{downloadUpgrade.usedSections.length} premium section{downloadUpgrade.usedSections.length > 1 ? 's' : ''}</strong>:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 20 }}>
              {downloadUpgrade.usedSections.map(id => (
                <span key={id} style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>
                  {getPremiumSectionLabel(id)}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, marginBottom: 24 }}>
              Switch to a <strong style={{ color: '#111827' }}>Pro account</strong> to download PDF files with premium sections.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDownloadUpgrade(null)} style={{ flex: 1, padding: '11px', border: '1.5px solid #E5E7EB', borderRadius: 10, background: 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Keep Editing
              </button>
              <button onClick={() => { setDownloadUpgrade(null); navigate('/pricing'); }} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#F97316)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(239,29,40,0.30)' }}>
                Switch to Pro
              </button>
            </div>
          </div>
        </div>
      )}

      {aiBuildType && (
        <AIBuildModal
          docType={DASHBOARD_DOC_TABS.find(tab => tab.type === aiBuildType) || currentDocType}
          onClose={() => setAiBuildType(null)}
          onStart={handleStartAIBuild}
        />
      )}

    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const LegacyTemplateCard = ({ tmpl, onUse, locked, onUpgrade }) => {
  const [hovered, setHovered] = useState(false);
  const [docType, setDocType] = useState('resume');

  return (
    <div style={{ background: 'white', border: `2px solid ${hovered && !locked ? tmpl.color : '#F1F5F9'}`, borderRadius: 16, overflow: 'hidden', transition: 'all 0.2s', boxShadow: hovered ? `0 8px 28px ${tmpl.color}22` : '0 2px 8px rgba(0,0,0,0.04)', transform: hovered ? 'translateY(-3px)' : 'none', opacity: locked && !hovered ? 0.85 : 1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {/* Color bar */}
      <div style={{ height: 6, background: tmpl.color }} />
      {/* Preview area */}
      <div style={{ height: 110, background: `linear-gradient(135deg, ${tmpl.color}10, ${tmpl.color}05)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 4, background: tmpl.color, borderRadius: 2, margin: '0 auto 6px' }} />
          <div style={{ width: 56, height: 3, background: tmpl.color + '66', borderRadius: 2, margin: '0 auto 4px' }} />
          <div style={{ width: 44, height: 3, background: tmpl.color + '44', borderRadius: 2, margin: '0 auto 8px' }} />
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            {[28, 36, 24].map((w, i) => <div key={i} style={{ width: w, height: 2, background: tmpl.color + '33', borderRadius: 1 }} />)}
          </div>
        </div>
        {locked && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 22 }}>🔒</span>
          </div>
        )}
      </div>
      <div style={{ padding: '14px 14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>{tmpl.name}</span>
          <span style={{ fontSize: 10, background: '#D1FAE5', color: '#065F46', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>Free</span>
        </div>
        <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 12, lineHeight: 1.4 }}>{tmpl.desc}</div>

        {!locked ? (
          <>
            <select value={docType} onChange={e => setDocType(e.target.value)}
              style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: '6px 8px', fontSize: 11, color: '#374151', marginBottom: 8, cursor: 'pointer', outline: 'none' }}>
              <option value="resume">Resume</option>
              <option value="cover_letter">Cover Letter</option>
              <option value="cv">Academic CV</option>
            </select>
            <button onClick={() => onUse(tmpl.id, docType)}
              style={{ width: '100%', background: tmpl.color, color: 'white', border: 'none', borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Use This Template →
            </button>
          </>
        ) : (
          <button onClick={onUpgrade}
            style={{ width: '100%', background: 'linear-gradient(135deg,#FB923C,#F97316)', color: 'white', border: 'none', borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            All templates are free
          </button>
        )}
      </div>
    </div>
  );
};

const CL_SAMPLE_CONTENT = {
  personalInfo: { fullName: 'Sarah Chen', email: 'sarah@email.com', phone: '+1 555-234-9608', location: '123 Anywhere St., Any City', jobTitle: 'Senior Software Engineer', website: 'www.sarahjohnson.com', accentColor: '' },
  jobInfo: { company: 'Salford & Co', role: 'Account Executive', hiringManager: 'Olivia Wilson' },
  content: {
    opening: 'I am reaching out to express my interest in the Account Executive position at Salford & Co, as per the advertisement. Holding a Bachelor\'s Degree in Science in Accounting with a specialization in Financial Management from the Business University, I am excited to bring my expertise, skills, and dedication to excellence to your company\'s dynamic environment.',
    body: 'Throughout my academic journey, I have developed a strong foundation in financial analysis, strategic planning, and client relationship management.\n\nMoreover, my previous role at Borcelle Corporation has given me practical experience in client management and business development.',
    closing: 'I have attached my resume for a more detailed overview of my qualifications. I am eager to discuss how my skills and experiences can meet your team\'s requirements.',
  },
};

const CoverLetterTemplateCard = ({ tmpl, onUse }) => {
  const [hovered, setHovered] = useState(false);
  const previewData = { ...EMPTY_CL, template: tmpl.id, ...CL_SAMPLE_CONTENT, personalInfo: { ...CL_SAMPLE_CONTENT.personalInfo, accentColor: tmpl.color } };
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: 'white', border: `1px solid ${hovered ? tmpl.color + '55' : '#EAECF0'}`, borderRadius: 16, overflow: 'hidden', transition: 'all 0.2s', boxShadow: hovered ? `0 12px 32px ${tmpl.color}22` : '0 1px 4px rgba(15,23,42,0.05)', transform: hovered ? 'translateY(-4px)' : 'none' }}>
      {/* Preview */}
      <div style={{ position: 'relative', height: 340, background: '#F8FAFC', overflow: 'hidden', cursor: 'pointer' }}
        onClick={() => onUse(tmpl.id, 'cover_letter')}>
        <div style={{ width: 700, position: 'absolute', top: 0, left: '50%', marginLeft: -350, transform: 'scale(0.44)', transformOrigin: 'top center', pointerEvents: 'none' }}>
          <ResumePreview data={previewData} id={`cl-template-preview-${tmpl.id}`} />
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: 'linear-gradient(to bottom, transparent, #F8FAFC)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,18,35,0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s', backdropFilter: 'blur(1px)' }}>
          <div style={{ background: 'white', color: '#111827', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 600, fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>Preview template</div>
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: '14px 16px 16px', borderTop: '1px solid #F1F3F5' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${tmpl.color}14`, color: tmpl.color, borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700, border: `1px solid ${tmpl.color}28`, letterSpacing: '0.02em' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: tmpl.color, flexShrink: 0 }} />
            {tmpl.name}
          </span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{tmpl.name} Cover Letter</div>
        <div style={{ fontSize: 11, color: '#C0C9D8', marginBottom: 14 }}>Professional layout · ATS-friendly</div>
        <button type="button" onClick={() => onUse(tmpl.id, 'cover_letter')}
          style={{ width: '100%', background: 'linear-gradient(135deg, #111827, #374151)', color: 'white', border: 'none', borderRadius: 9, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
          onMouseEnter={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${tmpl.color}, ${tmpl.color}cc)`; e.currentTarget.style.boxShadow = `0 4px 14px ${tmpl.color}55`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #111827, #374151)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}>
          Use this template
        </button>
      </div>
    </div>
  );
};

const DOC_TYPE_LABEL = { resume: 'Resume', cover_letter: 'Cover Letter', cv: 'Academic CV' };

const TemplateCard = ({ tmpl, onUse, activeDocType = 'resume' }) => {
  const [hovered, setHovered] = useState(false);
  const example = TEMPLATE_EXAMPLES[tmpl.id] || {};
  const previewData = buildTemplateExample(tmpl);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: 'white', border: `1px solid ${hovered ? tmpl.color + '55' : '#EAECF0'}`, borderRadius: 16, overflow: 'hidden', transition: 'all 0.2s', boxShadow: hovered ? `0 12px 32px ${tmpl.color}22` : '0 1px 4px rgba(15,23,42,0.05)', transform: hovered ? 'translateY(-4px)' : 'none' }}>

      {/* Preview */}
      <div style={{ position: 'relative', height: 340, background: '#F8FAFC', overflow: 'hidden', cursor: 'pointer' }}
        onClick={() => onUse(tmpl.id, activeDocType)}>
        <div style={{ width: 700, position: 'absolute', top: 0, left: '50%', marginLeft: -350, transform: 'scale(0.44)', transformOrigin: 'top center', pointerEvents: 'none' }}>
          <ResumePreview data={previewData} id={`template-preview-${tmpl.id}`} />
        </div>
        {/* gradient fade bottom */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: 'linear-gradient(to bottom, transparent, #F8FAFC)', pointerEvents: 'none' }} />
        {/* NEW badge */}
        {tmpl.isNew && (
          <div style={{ position: 'absolute', top: 12, right: 12, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', borderRadius: 6, padding: '3px 9px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', boxShadow: '0 2px 8px rgba(239,29,40,0.35)' }}>
            NEW
          </div>
        )}
        {/* hover overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,18,35,0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s', backdropFilter: 'blur(1px)' }}>
          <div style={{ background: 'white', color: '#111827', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 600, fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
            Preview template
          </div>
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '14px 16px 16px', borderTop: '1px solid #F1F3F5' }}>
        {/* name chip + desc */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${tmpl.color}14`, color: tmpl.color, borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700, border: `1px solid ${tmpl.color}28`, letterSpacing: '0.02em' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: tmpl.color, flexShrink: 0 }} />
            {tmpl.name}
          </span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {example.title || `${tmpl.name} Resume Example`}
        </div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>{tmpl.desc}</div>

        <button type="button" onClick={() => onUse(tmpl.id, activeDocType)}
          style={{ width: '100%', background: 'linear-gradient(135deg, #111827, #374151)', color: 'white', border: 'none', borderRadius: 9, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
          onMouseEnter={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${tmpl.color}, ${tmpl.color}cc)`; e.currentTarget.style.boxShadow = `0 4px 14px ${tmpl.color}55`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #111827, #374151)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}>
          Use as {DOC_TYPE_LABEL[activeDocType]}
        </button>
      </div>
    </div>
  );
};
